import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAdmin, PricingPlanItem, FormSubmission, ThemeConfig, HomeSectionConfig } from "../../context/AdminContext";
import { useTheme } from "../../context/ThemeContext";
import {
  LayoutDashboard,
  Boxes,
  Briefcase,
  CreditCard,
  Home,
  FileText,
  Menu as MenuIcon,
  Mail,
  Users,
  Server,
  LogOut,
  ExternalLink,
  Plus,
  Trash2,
  Edit3,
  Search,
  CheckCircle2,
  Save,
  RotateCcw,
  Download,
  Upload,
  Sun,
  Moon,
  Sparkles,
  X,
  Globe,
  Send,
  Shield,
  Inbox,
  Palette,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Smartphone,
  Phone,
  FileSpreadsheet,
  Layers,
  UploadCloud,
  GripVertical,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  FolderPlus
} from "lucide-react";

type ActiveTab =
  | "overview"
  | "inquiries"
  | "products"
  | "portfolio"
  | "pricing"
  | "home"
  | "theme"
  | "pages"
  | "menu"
  | "smtp"
  | "users"
  | "deploy";

export default function AdminDashboard() {
  const {
    currentUser,
    logout,
    apps,
    websites,
    software,
    addProduct,
    updateProduct,
    deleteProduct,
    toggleProductVisibility,
    submissions,
    isSyncingSubmissions,
    syncSubmissions,
    testSmtpConnection,
    updateSubmissionStatus,
    deleteSubmission,
    clearSubmissions,
    themeConfig,
    updateThemeConfig,
    resetThemeConfig,
    portfolio,
    portfolioCategories,
    addPortfolio,
    updatePortfolio,
    deletePortfolio,
    togglePortfolioVisibility,
    addPortfolioCategory,
    pricingPlans,
    pricingCategories,
    webPlans,
    appPlans,
    packagePlans,
    addPricingPlan,
    updatePricingPlan,
    deletePricingPlan,
    togglePricingVisibility,
    addPricingCategory,
    homeConfig,
    updateHomeConfig,
    homeSections,
    updateHomeSection,
    reorderHomeSection,
    toggleHomeSectionVisibility,
    pages,
    updatePage,
    menuItems,
    updateMenuItems,
    addMenuItem,
    deleteMenuItem,
    smtpConfig,
    updateSmtpConfig,
    users,
    addUser,
    deleteUser,
    resetDefaults,
    exportDatabase,
    importDatabase
  } = useAdmin();

  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<ActiveTab>("overview");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  useEffect(() => {
    if (activeTab === "inquiries") {
      syncSubmissions();
    }
  }, [activeTab, syncSubmissions]);

  // ----------------------------------------------------
  // GENERAL IMAGE FILE UPLOADER HELPER (PC -> Base64)
  // ----------------------------------------------------
  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    onComplete: (dataUrl: string) => void
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      alert("File size exceeds 4MB. Please choose an optimized image under 4MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        onComplete(reader.result);
        showToast(`Image "${file.name}" loaded successfully!`);
      }
    };
    reader.readAsDataURL(file);
  };

  // ----------------------------------------------------
  // FORM SUBMISSIONS & LEADS TAB STATE
  // ----------------------------------------------------
  const [inquirySearch, setInquirySearch] = useState("");
  const [inquiryFilter, setInquiryFilter] = useState("all");
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmission | null>(null);

  const newSubmissionsCount = (submissions || []).filter((s) => s.status === "New").length;

  const filteredSubmissions = (submissions || []).filter((sub) => {
    const matchesFilter =
      inquiryFilter === "all" ||
      (inquiryFilter === "new" && sub.status === "New") ||
      sub.formType.toLowerCase().includes(inquiryFilter.toLowerCase()) ||
      sub.status.toLowerCase() === inquiryFilter.toLowerCase();

    const term = inquirySearch.toLowerCase();
    const matchesSearch =
      sub.fullName.toLowerCase().includes(term) ||
      sub.email.toLowerCase().includes(term) ||
      (sub.phone && sub.phone.toLowerCase().includes(term)) ||
      (sub.company && sub.company.toLowerCase().includes(term)) ||
      (sub.service && sub.service.toLowerCase().includes(term));

    return matchesFilter && matchesSearch;
  });

  const handleExportSubmissionsCsv = () => {
    const headers = ["ID", "Form Type", "Status", "Full Name", "Email", "Phone", "Company", "Service", "Budget", "Date / Time", "Notes", "Submitted At"];
    const rows = (submissions || []).map((s) => [
      `"${s.id}"`,
      `"${s.formType}"`,
      `"${s.status}"`,
      `"${s.fullName}"`,
      `"${s.email}"`,
      `"${s.phone || ''}"`,
      `"${s.company || ''}"`,
      `"${s.service || ''}"`,
      `"${s.budget || ''}"`,
      `"${s.selectedDate ? `${s.selectedDate} ${s.selectedTime || ''}` : ''}"`,
      `"${(s.notes || '').replace(/"/g, '""')}"`,
      `"${s.submittedAt}"`
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `hickter-form-leads-${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("Leads & Form Submissions exported to CSV!");
  };

  // ----------------------------------------------------
  // PRODUCTS TAB STATE
  // ----------------------------------------------------
  const [productType, setProductType] = useState<"apps" | "websites" | "software">("apps");
  const [productSearch, setProductSearch] = useState("");
  const [editingProduct, setEditingProduct] = useState<any | null>(null);
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({
    title: "",
    category: "ecommerce",
    description: "",
    tags: "React, Node.js, TailwindCSS",
    url: "https://",
    playStoreUrl: "",
    websiteUrl: "",
    apkUrl: "",
    detailsUrl: "",
    status: "Live Production App",
    customBadge: "Google PlayStore Available",
    iconUrl: "/assets/logo/ecom.png",
    statLabel: "Sync Engine",
    statValue: "Real-Time Sync",
    features: "Real-Time Sync, Fully Customizable, App + Website, Orders & Inventory",
    showPlaystoreBtn: true,
    showWebsiteBtn: true,
    showDetailsBtn: true,
    isVisible: true
  });

  const handleCreateProduct = (e: React.FormEvent) => {
    e.preventDefault();
    const formatted = {
      title: newProduct.title,
      category: newProduct.category,
      description: newProduct.description,
      tags: newProduct.tags.split(",").map((s) => s.trim()).filter(Boolean),
      url: newProduct.url,
      playStoreUrl: newProduct.playStoreUrl || undefined,
      websiteUrl: newProduct.websiteUrl || undefined,
      apkUrl: newProduct.apkUrl || undefined,
      detailsUrl: newProduct.detailsUrl || undefined,
      status: newProduct.status,
      customBadge: newProduct.customBadge || newProduct.status,
      iconUrl: newProduct.iconUrl,
      highlightStat: { label: newProduct.statLabel, value: newProduct.statValue },
      features: newProduct.features.split(",").map((s) => s.trim()).filter(Boolean),
      showPlaystoreBtn: newProduct.showPlaystoreBtn,
      showWebsiteBtn: newProduct.showWebsiteBtn,
      showDetailsBtn: newProduct.showDetailsBtn,
      isVisible: newProduct.isVisible
    };
    addProduct(productType, formatted);
    setIsAddProductOpen(false);
    setNewProduct({
      title: "",
      category: "ecommerce",
      description: "",
      tags: "React, Node.js, TailwindCSS",
      url: "https://",
      playStoreUrl: "",
      websiteUrl: "",
      apkUrl: "",
      detailsUrl: "",
      status: "Live Production App",
      customBadge: "Google PlayStore Available",
      iconUrl: "/assets/logo/ecom.png",
      statLabel: "Sync Engine",
      statValue: "Real-Time Sync",
      features: "Real-Time Sync, Fully Customizable, App + Website, Orders & Inventory",
      showPlaystoreBtn: true,
      showWebsiteBtn: true,
      showDetailsBtn: true,
      isVisible: true
    });
    showToast(`New ${productType.slice(0, -1)} product created and synced live!`);
  };

  const handleSaveProductEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProduct) return;
    const formatted = {
      ...editingProduct,
      tags: typeof editingProduct.tags === "string" 
        ? editingProduct.tags.split(",").map((s: string) => s.trim()).filter(Boolean) 
        : editingProduct.tags,
      features: typeof editingProduct.features === "string" 
        ? editingProduct.features.split(",").map((s: string) => s.trim()).filter(Boolean) 
        : editingProduct.features
    };
    updateProduct(productType, editingProduct.id, formatted);
    setEditingProduct(null);
    showToast(`Product #${editingProduct.id} updated successfully!`);
  };

  // ----------------------------------------------------
  // THEME & SITE CUSTOMIZER STATE
  // ----------------------------------------------------
  const [themeForm, setThemeForm] = useState<ThemeConfig>(themeConfig || {
    primaryColor: "#2563eb",
    accentColor: "#06b6d4",
    fontHeading: "'Space Grotesk', sans-serif",
    fontBody: "'DM Sans', sans-serif",
    borderRadius: "0.75rem",
    siteName: "Hickter | Technology & Solutions",
    siteLogoUrl: "/assets/logo/dl.webp",
    themePreset: "cyber-blue"
  });

  const themePresets = [
    { name: "Cyber Blue", primary: "#2563eb", accent: "#06b6d4", preset: "cyber-blue" },
    { name: "Emerald Green", primary: "#10b981", accent: "#059669", preset: "emerald-green" },
    { name: "Violet Purple", primary: "#7c3aed", accent: "#a855f7", preset: "violet-purple" },
    { name: "Electric Cyan", primary: "#0891b2", accent: "#06b6d4", preset: "custom" },
    { name: "Sunset Orange", primary: "#ea580c", accent: "#f97316", preset: "sunset-orange" },
    { name: "Crimson Red", primary: "#e11d48", accent: "#f43f5e", preset: "crimson-red" },
    { name: "Golden Amber", primary: "#d97706", accent: "#f59e0b", preset: "custom" },
    { name: "Midnight Indigo", primary: "#4338ca", accent: "#6366f1", preset: "custom" }
  ];

  const handleSaveTheme = () => {
    updateThemeConfig(themeForm);
    showToast("Site styling and theme successfully updated globally!");
  };

  const handleResetTheme = () => {
    resetThemeConfig();
    setThemeForm({
      primaryColor: "#2563eb",
      accentColor: "#06b6d4",
      fontHeading: "'Space Grotesk', sans-serif",
      fontBody: "'DM Sans', sans-serif",
      borderRadius: "0.75rem",
      siteName: "Hickter | Technology & Solutions",
      siteLogoUrl: "/assets/logo/dl.webp",
      themePreset: "cyber-blue"
    });
    showToast("Theme reset to official Hickter defaults.");
  };

  // ----------------------------------------------------
  // PORTFOLIO TAB STATE
  // ----------------------------------------------------
  const [portfolioSearch, setPortfolioSearch] = useState("");
  const [portfolioCat, setPortfolioCat] = useState("all");
  const [editingPortfolio, setEditingPortfolio] = useState<any | null>(null);
  const [isAddPortfolioOpen, setIsAddPortfolioOpen] = useState(false);
  const [isAddPortfolioCatOpen, setIsAddPortfolioCatOpen] = useState(false);
  const [newPortfolioCatInput, setNewPortfolioCatInput] = useState("");
  const [newPortfolio, setNewPortfolio] = useState({
    title: "",
    category: "wordpress",
    customCategory: "",
    descriptions: "",
    urldescriptions: "example.com",
    imageUrl: "/assets/portfolio/all/bhandari.webp",
    url: "https://example.com",
    androidUrl: "",
    iosUrl: "",
    customBadge: "",
    isVisible: true
  });

  const handleCreatePortfolio = (e: React.FormEvent) => {
    e.preventDefault();
    const finalCategory = (newPortfolio.customCategory.trim() || newPortfolio.category).toLowerCase();
    if (newPortfolio.customCategory.trim()) {
      addPortfolioCategory(finalCategory);
    }
    addPortfolio({
      ...newPortfolio,
      category: finalCategory,
      isVisible: newPortfolio.isVisible !== false
    });
    setIsAddPortfolioOpen(false);
    setNewPortfolio({
      title: "",
      category: "wordpress",
      customCategory: "",
      descriptions: "",
      urldescriptions: "example.com",
      imageUrl: "/assets/portfolio/all/bhandari.webp",
      url: "https://example.com",
      androidUrl: "",
      iosUrl: "",
      customBadge: "",
      isVisible: true
    });
    showToast("New portfolio project added to live showcase!");
  };

  const handleSavePortfolioEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPortfolio) return;
    const finalCategory = (editingPortfolio.customCategory?.trim() || editingPortfolio.category).toLowerCase();
    if (editingPortfolio.customCategory?.trim()) {
      addPortfolioCategory(finalCategory);
    }
    updatePortfolio(editingPortfolio.id, {
      ...editingPortfolio,
      category: finalCategory
    });
    setEditingPortfolio(null);
    showToast(`Portfolio #${editingPortfolio.id} updated!`);
  };

  // ----------------------------------------------------
  // PRICING TAB STATE
  // ----------------------------------------------------
  const [pricingCat, setPricingCat] = useState<string>("all");
  const [editingPlan, setEditingPlan] = useState<PricingPlanItem | null>(null);
  const [isAddPlanOpen, setIsAddPlanOpen] = useState(false);
  const [isAddPricingCatOpen, setIsAddPricingCatOpen] = useState(false);
  const [newPricingCatInput, setNewPricingCatInput] = useState("");
  const [newPlan, setNewPlan] = useState({
    title: "",
    subTitle: "",
    category: "web",
    customCategory: "",
    inrPrice: "19,999",
    usdPrice: "249",
    billingCycle: "One-time",
    link: "/apply/package",
    features: "Custom Responsive UI/UX, Dynamic APIs, Admin Panel, 24/7 SLA Support",
    techStack: "React, Node.js, TailwindCSS",
    popular: false,
    customBadge: "",
    imageUrl: "",
    buttonText: "Choose Plan",
    isVisible: true
  });

  const handleCreatePlan = (e: React.FormEvent) => {
    e.preventDefault();
    const finalCategory = (newPlan.customCategory.trim() || newPlan.category).toLowerCase();
    if (newPlan.customCategory.trim()) {
      addPricingCategory(finalCategory);
    }
    const formatted: Omit<PricingPlanItem, "id"> = {
      title: newPlan.title,
      subTitle: newPlan.subTitle,
      category: finalCategory,
      inrPrice: newPlan.inrPrice,
      usdPrice: newPlan.usdPrice,
      billingCycle: newPlan.billingCycle,
      link: newPlan.link,
      features: newPlan.features.split(",").map((s) => s.trim()).filter(Boolean),
      techStack: newPlan.techStack.split(",").map((s) => s.trim()).filter(Boolean),
      popular: newPlan.popular,
      customBadge: newPlan.customBadge || undefined,
      imageUrl: newPlan.imageUrl || undefined,
      buttonText: newPlan.buttonText || "Choose Plan",
      isVisible: newPlan.isVisible !== false
    };
    addPricingPlan(finalCategory, formatted);
    setIsAddPlanOpen(false);
    setNewPlan({
      title: "",
      subTitle: "",
      category: "web",
      customCategory: "",
      inrPrice: "19,999",
      usdPrice: "249",
      billingCycle: "One-time",
      link: "/apply/package",
      features: "Custom Responsive UI/UX, Dynamic APIs, Admin Panel, 24/7 SLA Support",
      techStack: "React, Node.js, TailwindCSS",
      popular: false,
      customBadge: "",
      imageUrl: "",
      buttonText: "Choose Plan",
      isVisible: true
    });
    showToast(`New ${finalCategory} pricing plan created!`);
  };

  // ----------------------------------------------------
  // HOME PAGE BUILDER & REORDERING STATE
  // ----------------------------------------------------
  const [homeForm, setHomeForm] = useState(homeConfig);
  const [editingSection, setEditingSection] = useState<HomeSectionConfig | null>(null);
  const [draggedSectionIndex, setDraggedSectionIndex] = useState<number | null>(null);

  const handleSaveHome = (e: React.FormEvent) => {
    e.preventDefault();
    updateHomeConfig(homeForm);
    showToast("Home page hero, metrics & announcement bar updated!");
  };

  const handleSaveSectionEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSection) return;
    updateHomeSection(editingSection.id, editingSection);
    setEditingSection(null);
    showToast(`Section "${editingSection.name}" updated!`);
  };

  const moveSection = (index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= (homeSections || []).length) return;
    reorderHomeSection(index, targetIndex);
    showToast("Home sections order updated!");
  };

  // ----------------------------------------------------
  // SMTP CONFIG STATE
  // ----------------------------------------------------
  const [smtpForm, setSmtpForm] = useState(smtpConfig);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    if (smtpConfig) {
      setSmtpForm(smtpConfig);
    }
  }, [smtpConfig]);

  const handleSaveSmtp = (e: React.FormEvent) => {
    e.preventDefault();
    updateSmtpConfig(smtpForm);
    showToast("Email SMTP configuration saved successfully!");
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    setTestResult(null);
    try {
      const res = await testSmtpConnection(smtpForm);
      if (res && res.success) {
        setTestResult(`✓ SMTP handshake test passed with ${smtpForm.host}:${smtpForm.port} (${smtpForm.encryption}). Live test email dispatched to ${smtpForm.adminEmail}`);
        showToast("Live SMTP test dispatched successfully!");
      } else {
        setTestResult(`✗ SMTP handshake error: ${res?.message || (res as any)?.error || "Failed to reach SMTP endpoint"}`);
      }
    } catch (e: any) {
      setTestResult(`✗ Connection error: ${e.message}`);
    } finally {
      setTestingSmtp(false);
    }
  };

  // ----------------------------------------------------
  // USER MANAGEMENT STATE
  // ----------------------------------------------------
  const [newUser, setNewUser] = useState({
    name: "",
    username: "",
    email: "",
    role: "Admin" as any,
    status: "Active" as any
  });
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);

  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault();
    addUser(newUser);
    setIsAddUserOpen(false);
    setNewUser({ name: "", username: "", email: "", role: "Admin", status: "Active" });
    showToast(`User ${newUser.username} added to system!`);
  };

  // ----------------------------------------------------
  // CUSTOM MENU STATE
  // ----------------------------------------------------
  const [newMenuItem, setNewMenuItem] = useState({
    name: "",
    path: "/custom-page",
    iconName: "Globe",
    isVisible: true,
    order: menuItems.length + 1,
    isCustom: true
  });
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);

  const handleAddMenuItem = (e: React.FormEvent) => {
    e.preventDefault();
    addMenuItem(newMenuItem);
    setIsAddMenuOpen(false);
    setNewMenuItem({
      name: "",
      path: "/custom-page",
      iconName: "Globe",
      isVisible: true,
      order: menuItems.length + 2,
      isCustom: true
    });
    showToast("New navigation link added to site header!");
  };

  // ----------------------------------------------------
  // DEPLOY & BACKUP STATE
  // ----------------------------------------------------
  const [isPurgingCache, setIsPurgingCache] = useState(false);
  const [cacheStatus, setCacheStatus] = useState<string | null>(null);
  const [importJsonText, setImportJsonText] = useState("");

  const handleDownloadBackup = () => {
    const json = exportDatabase();
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hickter-database-backup-${new Date().toISOString().split("T")[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast("Database exported successfully!");
  };

  const handleImportJson = () => {
    if (!importJsonText.trim()) return;
    const res = importDatabase(importJsonText);
    if (res.success) {
      showToast(res.message);
      setImportJsonText("");
    } else {
      alert(res.message);
    }
  };

  const handlePurgeCache = () => {
    setIsPurgingCache(true);
    setCacheStatus("Syncing local storage cache with production assets...");
    setTimeout(() => {
      setIsPurgingCache(false);
      setCacheStatus("All caches purged and state re-synchronized across DOM!");
      showToast("Cache purged & site re-synced!");
    }, 1200);
  };

  const currentProductsList = productType === "apps" ? apps : productType === "websites" ? websites : software;
  const filteredProducts = currentProductsList.filter((item: any) =>
    item.title.toLowerCase().includes(productSearch.toLowerCase()) ||
    item.description?.toLowerCase().includes(productSearch.toLowerCase())
  );

  const distinctPortfolioCats = Array.from(
    new Set(["all", ...(portfolioCategories || []), ...portfolio.map((p) => (p.category || "").toLowerCase())])
  ).filter(Boolean);

  const filteredPortfolio = portfolio.filter((item) => {
    const matchesCat = portfolioCat === "all" || (item.category || "").toLowerCase() === portfolioCat.toLowerCase();
    const matchesSearch =
      item.title.toLowerCase().includes(portfolioSearch.toLowerCase()) ||
      item.descriptions?.toLowerCase().includes(portfolioSearch.toLowerCase());
    return matchesCat && matchesSearch;
  });

  const distinctPricingCats = Array.from(
    new Set(["all", ...(pricingCategories || ["web", "app", "package"]), ...(pricingPlans || []).map((p) => (p.category || "web").toLowerCase())])
  ).filter(Boolean);

  const filteredPricing = (pricingPlans || []).filter((plan) => {
    if (pricingCat === "all") return true;
    return (plan.category || "web").toLowerCase() === pricingCat.toLowerCase();
  });

  const sidebarItems = [
    { id: "overview", label: "Dashboard", icon: <LayoutDashboard size={18} /> },
    { id: "inquiries", label: "Form Inquiries", icon: <Inbox size={18} />, badge: newSubmissionsCount > 0 ? `${newSubmissionsCount} NEW` : (submissions || []).length },
    { id: "products", label: "Products Manager", icon: <Boxes size={18} />, badge: apps.length + websites.length + software.length },
    { id: "portfolio", label: "Portfolio Projects", icon: <Briefcase size={18} />, badge: portfolio.length },
    { id: "pricing", label: "Pricing & Plans", icon: <CreditCard size={18} />, badge: (pricingPlans || []).length },
    { id: "theme", label: "Site Styling & Theme", icon: <Palette size={18} /> },
    { id: "home", label: "Home Page Builder", icon: <Home size={18} /> },
    { id: "pages", label: "Page SEO & Meta", icon: <FileText size={18} />, badge: pages.length },
    { id: "menu", label: "Custom Menu Nav", icon: <MenuIcon size={18} />, badge: menuItems.length },
    { id: "smtp", label: "Email SMTP Server", icon: <Mail size={18} /> },
    { id: "users", label: "User Management", icon: <Users size={18} />, badge: users.length },
    { id: "deploy", label: "Deploy & Backup", icon: <Server size={18} /> }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-100 font-sans flex flex-col transition-colors duration-300">
      {/* TOAST NOTIFICATION */}
      {toastMessage && (
        <div className="fixed top-5 right-5 z-[500] px-4 py-3 rounded-2xl bg-blue-600 text-white font-bold text-xs shadow-2xl flex items-center gap-2 animate-bounce">
          <Sparkles size={16} />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* TOP ADMIN HEADER BAR */}
      <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 sm:px-6 flex items-center justify-between shrink-0 sticky top-0 z-[120] shadow-xs">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 lg:hidden cursor-pointer"
          >
            <MenuIcon size={20} />
          </button>

          <Link to="/" className="flex items-center gap-2.5 group">
            <img src="/assets/logo/dl.webp" alt="Hickter" className="h-8 object-contain transition-transform group-hover:scale-105" />
            <div className="hidden sm:block">
              <span className="font-extrabold text-sm text-slate-900 dark:text-white tracking-tight block leading-none">
                Hickter Core
              </span>
              <span className="text-[10px] text-blue-600 dark:text-cyan-400 font-bold uppercase tracking-wider block">
                Master Admin Engine
              </span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-2 ml-4 pl-4 border-l border-slate-200 dark:border-slate-800">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              Live Synced with Website
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2.5 sm:gap-3">
          {/* Quick link to live site */}
          <Link
            to="/"
            target="_blank"
            className="px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <Globe size={14} className="text-blue-600 dark:text-cyan-400" />
            <span className="hidden sm:inline">View Live Site</span>
            <ExternalLink size={12} />
          </Link>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-cyan-400 transition-colors cursor-pointer"
            title="Toggle Light/Dark Theme"
          >
            {theme === "dark" ? <Sun size={17} className="text-amber-400" /> : <Moon size={17} />}
          </button>

          {/* User Chip */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20">
            <Shield size={14} className="text-blue-600 dark:text-cyan-400" />
            <div className="text-left hidden sm:block">
              <span className="text-xs font-extrabold text-blue-700 dark:text-cyan-300 block leading-none">
                {currentUser?.username || "superadmin"}
              </span>
              <span className="text-[9px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider block">
                {currentUser?.role || "Super Admin"}
              </span>
            </div>
          </div>

          {/* Logout */}
          <button
            onClick={() => {
              if (window.confirm("Are you sure you want to sign out of the Admin Panel?")) {
                logout();
                navigate("/admin");
              }
            }}
            className="p-2 rounded-xl bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/20 transition-all cursor-pointer"
            title="Sign Out"
          >
            <LogOut size={17} />
          </button>
        </div>
      </header>

      {/* MAIN LAYOUT WRAPPER */}
      <div className="flex-1 flex overflow-hidden">
        {/* SIDEBAR NAVIGATION */}
        <aside
          className={`fixed lg:static inset-y-16 left-0 z-[115] w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col justify-between transition-transform duration-300 shadow-xl lg:shadow-none ${
            mobileSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
        >
          <div className="p-3 space-y-1 overflow-y-auto flex-1">
            <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 dark:text-slate-500 px-3 py-2 block">
              Core Modules
            </span>

            {sidebarItems.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id as ActiveTab);
                    setMobileSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    isActive
                      ? "bg-blue-600 dark:bg-blue-500 !text-white shadow-md shadow-blue-500/25 font-black"
                      : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 hover:text-slate-950 dark:hover:text-white"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={isActive ? "!text-white" : "text-blue-600 dark:text-cyan-400"}>
                      {item.icon}
                    </span>
                    <span className={isActive ? "!text-white font-extrabold" : ""}>{item.label}</span>
                  </div>

                  {item.badge !== undefined && (
                    <span
                      className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                        isActive
                          ? "bg-white/20 !text-white"
                          : "bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
                      }`}
                    >
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Quick Database Backup Footer */}
          <div className="p-3 border-t border-slate-200 dark:border-slate-800 space-y-2">
            <button
              onClick={handleDownloadBackup}
              className="w-full py-2 px-3 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer"
            >
              <Download size={14} className="text-blue-600 dark:text-cyan-400" />
              <span>Export JSON Backup</span>
            </button>
            <button
              onClick={() => {
                if (window.confirm("Restore factory default site data? Any unsaved local edits will be reset.")) {
                  resetDefaults();
                  showToast("Defaults restored successfully!");
                }
              }}
              className="w-full py-2 px-3 rounded-xl bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer"
            >
              <RotateCcw size={13} />
              <span>Reset Defaults</span>
            </button>
          </div>
        </aside>

        {/* MAIN CONTENT AREA */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">
          
          {/* ======================================================== */}
          {/* TAB 1: OVERVIEW DASHBOARD */}
          {/* ======================================================== */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Welcome Banner */}
              <div className="p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 text-white shadow-xl relative overflow-hidden">
                <div className="absolute right-0 top-0 bottom-0 w-96 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.15),transparent_70%)] pointer-events-none" />
                <div className="relative z-10 space-y-2 max-w-2xl">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-xs font-bold text-white">
                    <Sparkles size={14} />
                    <span>Real-Time Sync Activated</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
                    Welcome to Hickter Master Control Panel
                  </h2>
                  <p className="text-white/80 text-xs sm:text-sm font-medium leading-relaxed">
                    All additions, pricing edits, new product releases (Apps, Websites, Software), portfolio showcases, and navigation links update instantly on your live web domain.
                  </p>
                </div>
              </div>

              {/* KPI Cards Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                <div
                  onClick={() => setActiveTab("inquiries")}
                  className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-2 cursor-pointer hover:border-blue-500/50 transition-all"
                >
                  <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
                    <span className="text-xs font-bold">Form Inquiries</span>
                    <Inbox size={18} className="text-blue-600 dark:text-cyan-400" />
                  </div>
                  <div className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-2">
                    <span>{(submissions || []).length}</span>
                    {newSubmissionsCount > 0 && (
                      <span className="text-[10px] font-extrabold bg-blue-600 text-white px-2 py-0.5 rounded-full animate-pulse">
                        {newSubmissionsCount} NEW
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Contact, Meetings & Plan Leads
                  </p>
                </div>

                <div
                  onClick={() => setActiveTab("products")}
                  className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-2 cursor-pointer hover:border-blue-500/50 transition-all"
                >
                  <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
                    <span className="text-xs font-bold">Total Products</span>
                    <Boxes size={18} className="text-blue-600 dark:text-cyan-400" />
                  </div>
                  <div className="text-2xl font-black text-slate-900 dark:text-white">
                    {apps.length + websites.length + software.length}
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    {apps.length} Apps • {websites.length} Websites • {software.length} Software
                  </p>
                </div>

                <div
                  onClick={() => setActiveTab("portfolio")}
                  className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-2 cursor-pointer hover:border-purple-500/50 transition-all"
                >
                  <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
                    <span className="text-xs font-bold">Portfolio Showcase</span>
                    <Briefcase size={18} className="text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="text-2xl font-black text-slate-900 dark:text-white">
                    {portfolio.length}
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Active client projects & live links
                  </p>
                </div>

                <div
                  onClick={() => setActiveTab("pricing")}
                  className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-2 cursor-pointer hover:border-emerald-500/50 transition-all"
                >
                  <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
                    <span className="text-xs font-bold">Pricing Tiers</span>
                    <CreditCard size={18} className="text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="text-2xl font-black text-slate-900 dark:text-white">
                    {webPlans.length + appPlans.length + packagePlans.length}
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Web, Mobile & Turnkey Packages
                  </p>
                </div>

                <div
                  onClick={() => setActiveTab("users")}
                  className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-2 cursor-pointer hover:border-amber-500/50 transition-all"
                >
                  <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
                    <span className="text-xs font-bold">System Users</span>
                    <Users size={18} className="text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="text-2xl font-black text-slate-900 dark:text-white">
                    {users.length}
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Super Admin & Staff roles
                  </p>
                </div>
              </div>

              {/* Quick Actions Shortcuts */}
              <div className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                <h3 className="text-sm font-extrabold text-slate-900 dark:text-white tracking-wide">
                  Quick Actions & Fast Triggers
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <button
                    onClick={() => setActiveTab("inquiries")}
                    className="p-3.5 rounded-2xl bg-cyan-50 dark:bg-cyan-500/10 hover:bg-cyan-100 dark:hover:bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 font-bold text-xs flex flex-col items-center justify-center gap-2 transition-all cursor-pointer border border-cyan-200 dark:border-cyan-500/20"
                  >
                    <Inbox size={18} />
                    <span>View Inquiries ({newSubmissionsCount} New)</span>
                  </button>

                  <button
                    onClick={() => {
                      setActiveTab("products");
                      setIsAddProductOpen(true);
                    }}
                    className="p-3.5 rounded-2xl bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 text-blue-700 dark:text-cyan-300 font-bold text-xs flex flex-col items-center justify-center gap-2 transition-all cursor-pointer border border-blue-200 dark:border-blue-500/20"
                  >
                    <Plus size={18} />
                    <span>Create New Product</span>
                  </button>

                  <button
                    onClick={() => {
                      setActiveTab("portfolio");
                      setIsAddPortfolioOpen(true);
                    }}
                    className="p-3.5 rounded-2xl bg-purple-50 dark:bg-purple-500/10 hover:bg-purple-100 dark:hover:bg-purple-500/20 text-purple-700 dark:text-purple-300 font-bold text-xs flex flex-col items-center justify-center gap-2 transition-all cursor-pointer border border-purple-200 dark:border-purple-500/20"
                  >
                    <Plus size={18} />
                    <span>Add Portfolio Project</span>
                  </button>

                  <button
                    onClick={() => setActiveTab("theme")}
                    className="p-3.5 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 font-bold text-xs flex flex-col items-center justify-center gap-2 transition-all cursor-pointer border border-indigo-200 dark:border-indigo-500/20"
                  >
                    <Palette size={18} />
                    <span>Colors & Fonts</span>
                  </button>

                  <button
                    onClick={() => setActiveTab("home")}
                    className="p-3.5 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-bold text-xs flex flex-col items-center justify-center gap-2 transition-all cursor-pointer border border-emerald-200 dark:border-emerald-500/20"
                  >
                    <Home size={18} />
                    <span>Edit Home Page Hero</span>
                  </button>

                  <button
                    onClick={() => setActiveTab("smtp")}
                    className="p-3.5 rounded-2xl bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100 dark:hover:bg-amber-500/20 text-amber-700 dark:text-amber-300 font-bold text-xs flex flex-col items-center justify-center gap-2 transition-all cursor-pointer border border-amber-200 dark:border-amber-500/20"
                  >
                    <Mail size={18} />
                    <span>Test SMTP Server</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB: FORM INQUIRIES & CLIENT LEADS */}
          {/* ======================================================== */}
          {activeTab === "inquiries" && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                      Form Inquiries & Client Leads
                    </h2>
                    {newSubmissionsCount > 0 && (
                      <span className="px-2.5 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-black tracking-wide uppercase shadow-sm animate-pulse">
                        {newSubmissionsCount} NEW
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Live capture of all form submissions across Contact, Meeting Booking, Project Scope Enquiries, and Plan Applications.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      await syncSubmissions();
                      showToast("Form submissions synced live from server!");
                    }}
                    disabled={isSyncingSubmissions}
                    className="p-2.5 px-3 rounded-xl bg-blue-50 dark:bg-blue-500/10 border border-blue-300 dark:border-blue-500/30 text-blue-700 dark:text-cyan-400 text-xs font-bold flex items-center gap-1.5 hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-all cursor-pointer disabled:opacity-60"
                  >
                    <RefreshCw size={15} className={isSyncingSubmissions ? "animate-spin" : ""} />
                    <span>{isSyncingSubmissions ? "Syncing..." : "Sync Leads"}</span>
                  </button>
                  <button
                    onClick={handleExportSubmissionsCsv}
                    className="p-2.5 px-3 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400 text-xs font-bold flex items-center gap-1.5 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-all cursor-pointer"
                  >
                    <FileSpreadsheet size={15} />
                    <span>Export CSV</span>
                  </button>
                  {(submissions || []).length > 0 && (
                    <button
                      onClick={() => {
                        if (window.confirm("Are you sure you want to clear ALL form submissions? This cannot be undone.")) {
                          clearSubmissions();
                          showToast("All submissions cleared.");
                        }
                      }}
                      className="p-2.5 px-3 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-300 dark:border-red-500/30 text-red-600 dark:text-red-400 text-xs font-bold flex items-center gap-1.5 hover:bg-red-100 dark:hover:bg-red-500/20 transition-all cursor-pointer"
                    >
                      <Trash2 size={15} />
                      <span>Clear All</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Inquiries KPI Strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs">
                  <span className="text-[11px] font-bold text-slate-500 block">Total Submissions</span>
                  <span className="text-xl font-black text-slate-900 dark:text-white">{(submissions || []).length}</span>
                </div>
                <div className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 shadow-xs">
                  <span className="text-[11px] font-bold text-blue-600 dark:text-cyan-400 block">New / Uncontacted</span>
                  <span className="text-xl font-black text-blue-700 dark:text-cyan-300">{newSubmissionsCount}</span>
                </div>
                <div className="p-4 rounded-2xl bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/30 shadow-xs">
                  <span className="text-[11px] font-bold text-purple-600 dark:text-purple-400 block">Meeting Consultations</span>
                  <span className="text-xl font-black text-purple-700 dark:text-purple-300">
                    {(submissions || []).filter((s) => s.formType === "Meeting Booking").length}
                  </span>
                </div>
                <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 shadow-xs">
                  <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 block">Plan Applications</span>
                  <span className="text-xl font-black text-emerald-700 dark:text-emerald-300">
                    {(submissions || []).filter((s) => s.formType === "Plan Application").length}
                  </span>
                </div>
              </div>

              {/* Filters & Search Control */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-800">
                <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
                  {[
                    { id: "all", label: "All Forms" },
                    { id: "new", label: "New Only" },
                    { id: "Contact Form", label: "Contact" },
                    { id: "Meeting Booking", label: "Meeting" },
                    { id: "Quick Enquiry", label: "Enquiries" },
                    { id: "Plan Application", label: "Plans" },
                    { id: "Newsletter", label: "Newsletter" }
                  ].map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setInquiryFilter(f.id)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                        inquiryFilter === f.id
                          ? "bg-blue-600 dark:bg-blue-500 !text-white font-extrabold shadow-sm"
                          : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="relative w-full sm:w-72">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={inquirySearch}
                    onChange={(e) => setInquirySearch(e.target.value)}
                    placeholder="Search by name, email, phone, company..."
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl py-1.5 pl-8 pr-3 text-xs text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Submissions List / Table */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs">
                {filteredSubmissions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 font-extrabold uppercase text-[10px] tracking-wider">
                        <tr>
                          <th className="py-3 px-4">Date & Form Type</th>
                          <th className="py-3 px-4">Client Contact</th>
                          <th className="py-3 px-4">Service & Budget</th>
                          <th className="py-3 px-4">Details / Notes</th>
                          <th className="py-3 px-4">Status</th>
                          <th className="py-3 px-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {filteredSubmissions.map((sub) => (
                          <tr key={sub.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors">
                            <td className="py-3 px-4 align-top">
                              <span className="text-[10px] text-slate-400 dark:text-slate-500 font-mono block">
                                {sub.submittedAt}
                              </span>
                              <span className={`inline-block mt-1 px-2 py-0.5 rounded-md text-[10px] font-extrabold ${
                                sub.formType === "Meeting Booking"
                                  ? "bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-500/20"
                                  : sub.formType === "Plan Application"
                                  ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/20"
                                  : sub.formType === "Quick Enquiry"
                                  ? "bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-500/20"
                                  : "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/20"
                              }`}>
                                {sub.formType}
                              </span>
                            </td>

                            <td className="py-3 px-4 align-top space-y-0.5">
                              <p className="font-extrabold text-slate-900 dark:text-white text-xs">
                                {sub.fullName}
                              </p>
                              <a
                                href={`mailto:${sub.email}`}
                                className="text-[11px] text-blue-600 dark:text-cyan-400 hover:underline block"
                              >
                                {sub.email}
                              </a>
                              {sub.phone && (
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                                  {sub.phone}
                                </p>
                              )}
                              {sub.company && (
                                <span className="text-[10px] text-slate-400 block font-medium">
                                  🏢 {sub.company}
                                </span>
                              )}
                            </td>

                            <td className="py-3 px-4 align-top space-y-0.5 max-w-[200px]">
                              <p className="font-bold text-slate-800 dark:text-slate-200 text-xs truncate">
                                {sub.service || sub.planName || "General Tech Request"}
                              </p>
                              {sub.budget && (
                                <span className="text-[11px] font-extrabold text-emerald-600 dark:text-emerald-400 block">
                                  {sub.budget}
                                </span>
                              )}
                              {sub.selectedDate && (
                                <span className="text-[10px] font-mono text-purple-600 dark:text-purple-400 block">
                                  📅 {sub.selectedDate} {sub.selectedTime}
                                </span>
                              )}
                            </td>

                            <td className="py-3 px-4 align-top max-w-[260px]">
                              <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">
                                {sub.notes || "No additional brief."}
                              </p>
                            </td>

                            <td className="py-3 px-4 align-top">
                              <select
                                value={sub.status}
                                onChange={(e) => {
                                  updateSubmissionStatus(sub.id, e.target.value as FormSubmission["status"]);
                                  showToast(`Inquiry status updated to "${e.target.value}"`);
                                }}
                                className={`text-[11px] font-bold py-1 px-2.5 rounded-lg border focus:outline-none cursor-pointer ${
                                  sub.status === "New"
                                    ? "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-cyan-300 border-blue-300 dark:border-blue-500/30"
                                    : sub.status === "Contacted"
                                    ? "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-500/30"
                                    : sub.status === "In Progress"
                                    ? "bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-500/30"
                                    : "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-500/30"
                                }`}
                              >
                                <option value="New">● New</option>
                                <option value="Contacted">● Contacted</option>
                                <option value="In Progress">● In Progress</option>
                                <option value="Closed">● Closed</option>
                              </select>
                            </td>

                            <td className="py-3 px-4 align-top text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <button
                                  onClick={() => setSelectedSubmission(sub)}
                                  className="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 text-blue-600 dark:text-cyan-400 cursor-pointer"
                                  title="View Full Submission"
                                >
                                  <Eye size={14} />
                                </button>
                                {sub.phone && (
                                  <a
                                    href={`https://wa.me/${sub.phone.replace(/[^0-9]/g, "")}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="p-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 hover:bg-emerald-100 text-emerald-600 dark:text-emerald-400"
                                    title="WhatsApp Client"
                                  >
                                    <Phone size={14} />
                                  </a>
                                )}
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Delete submission from ${sub.fullName}?`)) {
                                      deleteSubmission(sub.id);
                                      showToast("Submission deleted.");
                                    }
                                  }}
                                  className="p-1.5 rounded-lg bg-red-50 dark:bg-red-500/10 hover:bg-red-100 text-red-600 dark:text-red-400 cursor-pointer"
                                  title="Delete Entry"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 space-y-2">
                    <Inbox size={28} className="mx-auto text-slate-400" />
                    <p className="text-xs font-bold text-slate-600 dark:text-slate-400">
                      No form submissions match the selected filter.
                    </p>
                  </div>
                )}
              </div>

              {/* VIEW FULL SUBMISSION DETAILS MODAL */}
              {selectedSubmission && (
                <div className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                  <div className="w-full max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl space-y-5">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-cyan-300">
                            {selectedSubmission.formType}
                          </span>
                          <span className="text-[10px] text-slate-400 font-mono">
                            {selectedSubmission.submittedAt}
                          </span>
                        </div>
                        <h3 className="text-base font-extrabold text-slate-900 dark:text-white mt-1">
                          {selectedSubmission.fullName}
                        </h3>
                      </div>
                      <button
                        onClick={() => setSelectedSubmission(null)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white cursor-pointer"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <div className="space-y-3 text-xs">
                      <div className="grid grid-cols-2 gap-3 p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 uppercase block">Email Address</span>
                          <a href={`mailto:${selectedSubmission.email}`} className="font-bold text-blue-600 dark:text-cyan-400 hover:underline">
                            {selectedSubmission.email}
                          </a>
                        </div>
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 uppercase block">Phone / WhatsApp</span>
                          <span className="font-bold text-slate-900 dark:text-white font-mono">
                            {selectedSubmission.phone || "Not provided"}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 uppercase block">Company / Business</span>
                          <span className="font-bold text-slate-900 dark:text-white">
                            {selectedSubmission.company || "Individual"}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 uppercase block">Status</span>
                          <span className="font-extrabold text-blue-600 dark:text-cyan-400">
                            ● {selectedSubmission.status}
                          </span>
                        </div>
                      </div>

                      {(selectedSubmission.service || selectedSubmission.budget || selectedSubmission.selectedDate) && (
                        <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-1.5">
                          {selectedSubmission.service && (
                            <div>
                              <span className="text-[10px] font-bold text-slate-400 uppercase block">Service / Project Scope</span>
                              <span className="font-bold text-slate-900 dark:text-white">{selectedSubmission.service}</span>
                            </div>
                          )}
                          {selectedSubmission.budget && (
                            <div>
                              <span className="text-[10px] font-bold text-slate-400 uppercase block">Proposed Budget</span>
                              <span className="font-extrabold text-emerald-600 dark:text-emerald-400">{selectedSubmission.budget}</span>
                            </div>
                          )}
                          {selectedSubmission.selectedDate && (
                            <div>
                              <span className="text-[10px] font-bold text-slate-400 uppercase block">Scheduled Date & Time</span>
                              <span className="font-bold text-purple-600 dark:text-purple-400">
                                📅 {selectedSubmission.selectedDate} at {selectedSubmission.selectedTime}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase block">Message / Detailed Scope</span>
                        <div className="p-3 rounded-2xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 leading-relaxed font-medium max-h-48 overflow-y-auto whitespace-pre-wrap">
                          {selectedSubmission.notes || "No additional message provided."}
                        </div>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {selectedSubmission.phone && (
                          <a
                            href={`https://wa.me/${selectedSubmission.phone.replace(/[^0-9]/g, "")}`}
                            target="_blank"
                            rel="noreferrer"
                            className="p-2 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1.5"
                          >
                            <Phone size={14} />
                            <span>WhatsApp</span>
                          </a>
                        )}
                        <a
                          href={`mailto:${selectedSubmission.email}`}
                          className="p-2 px-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs flex items-center gap-1.5"
                        >
                          <Mail size={14} />
                          <span>Send Email</span>
                        </a>
                      </div>

                      <button
                        onClick={() => setSelectedSubmission(null)}
                        className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 font-bold text-xs"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 2: PRODUCTS MANAGER */}
          {/* ======================================================== */}
          {activeTab === "products" && (
            <div className="space-y-6">
              {/* Product Subheader & Controls */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                    Products & Platform Ecosystem
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Create, modify, and delete digital products. Changes immediately reflect in /ourproducts/our-products-all
                  </p>
                </div>

                <button
                  onClick={() => setIsAddProductOpen(true)}
                  className="btn-primary-blue py-2.5 px-4 text-xs font-bold rounded-xl flex items-center gap-2 !text-white shadow-md shadow-blue-500/20 cursor-pointer"
                >
                  <Plus size={16} className="!text-white" />
                  <span className="!text-white">Create New Product</span>
                </button>
              </div>

              {/* Subtabs for Apps, Websites, Software */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  {(["apps", "websites", "software"] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => setProductType(type)}
                      className={`px-4 py-2 rounded-xl text-xs font-bold capitalize transition-all cursor-pointer ${
                        productType === type
                          ? "bg-blue-600 dark:bg-blue-500 !text-white shadow-sm font-extrabold"
                          : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                      }`}
                    >
                      {type} ({type === "apps" ? apps.length : type === "websites" ? websites.length : software.length})
                    </button>
                  ))}
                </div>

                {/* Search Bar */}
                <div className="relative w-full sm:w-64">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder={`Search ${productType}...`}
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl py-1.5 pl-8 pr-3 text-xs text-slate-900 dark:text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Products Table / Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProducts.map((p: any) => (
                  <div
                    key={p.id}
                    className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-between space-y-4 hover:border-blue-500/40 transition-all"
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 p-2 flex items-center justify-center shrink-0 border border-slate-200 dark:border-slate-700">
                          <img
                            src={p.iconUrl}
                            alt=""
                            className="w-full h-full object-contain rounded-lg"
                            onError={(e: any) => {
                              e.target.onerror = null;
                              e.target.src = "/assets/logo/dl.webp";
                            }}
                          />
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                          <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full border ${
                            p.isVisible === false
                              ? "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30"
                              : "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20"
                          }`}>
                            ● {p.isVisible === false ? "Hidden" : "Live"}
                          </span>
                          <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-700">
                            {p.customBadge || p.status}
                          </span>
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-extrabold text-slate-900 dark:text-white line-clamp-1">
                          {p.title}
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mt-1">
                          {p.description}
                        </p>
                      </div>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1">
                        {p.tags?.slice(0, 3).map((t: string, i: number) => (
                          <span
                            key={i}
                            className="text-[9px] font-semibold text-blue-700 dark:text-cyan-400 bg-blue-50 dark:bg-blue-500/10 px-2 py-0.5 rounded"
                          >
                            #{t}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {p.detailsUrl && (
                          <Link
                            to={p.detailsUrl}
                            target="_blank"
                            className="text-xs font-bold text-blue-600 dark:text-cyan-400 hover:underline flex items-center gap-1"
                          >
                            <span>Detail</span>
                            <ExternalLink size={11} />
                          </Link>
                        )}
                        {p.playStoreUrl && (
                          <a
                            href={p.playStoreUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-0.5"
                          >
                            <Smartphone size={11} />
                            <span>PlayStore</span>
                          </a>
                        )}
                      </div>

                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => {
                            toggleProductVisibility(productType, p.id);
                            showToast(p.isVisible === false ? `"${p.title}" is now VISIBLE on public site!` : `"${p.title}" is now HIDDEN from public site!`);
                          }}
                          className={`p-1.5 rounded-lg border transition-all cursor-pointer ${
                            p.isVisible === false
                              ? "bg-amber-50 dark:bg-amber-500/10 border-amber-300 dark:border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-100"
                              : "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-300 dark:border-emerald-500/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100"
                          }`}
                          title={p.isVisible === false ? "Hidden (Click to Show on Site)" : "Visible (Click to Hide from Site)"}
                        >
                          {p.isVisible === false ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button
                          onClick={() => setEditingProduct({
                            ...p,
                            tags: Array.isArray(p.tags) ? p.tags.join(", ") : p.tags,
                            features: Array.isArray(p.features) ? p.features.join(", ") : p.features,
                            customBadge: p.customBadge || p.status,
                            playStoreUrl: p.playStoreUrl || "",
                            websiteUrl: p.websiteUrl || "",
                            apkUrl: p.apkUrl || "",
                            detailsUrl: p.detailsUrl || "",
                            showPlaystoreBtn: p.showPlaystoreBtn !== false,
                            showWebsiteBtn: p.showWebsiteBtn !== false,
                            showDetailsBtn: p.showDetailsBtn !== false,
                            isVisible: p.isVisible !== false
                          })}
                          className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-blue-500/20 text-slate-600 dark:text-slate-300 hover:text-blue-600 cursor-pointer"
                          title="Edit Product & Links"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete product "${p.title}"?`)) {
                              deleteProduct(productType, p.id);
                              showToast(`Product deleted.`);
                            }
                          }}
                          className="p-1.5 rounded-lg bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 cursor-pointer"
                          title="Delete Product"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ADD PRODUCT MODAL */}
              {isAddProductOpen && (
                <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                  <div className="w-full max-w-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                      <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                        Create New {productType.slice(0, -1).toUpperCase()} Product
                      </h3>
                      <button
                        onClick={() => setIsAddProductOpen(false)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white cursor-pointer"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <form onSubmit={handleCreateProduct} className="space-y-4 text-xs">
                      {/* Image / Icon Uploader */}
                      <div className="space-y-1.5 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                        <label className="font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
                          <span>Product Icon / Brand Image</span>
                          <span className="text-[10px] text-blue-600 dark:text-cyan-400 font-semibold">Upload from PC or enter URL</span>
                        </label>
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-1 flex items-center justify-center shrink-0 overflow-hidden shadow-xs">
                            <img
                              src={newProduct.iconUrl}
                              alt="Preview"
                              className="w-full h-full object-contain"
                              onError={(e: any) => { e.target.src = "/assets/logo/dl.webp"; }}
                            />
                          </div>
                          <div className="flex-1 space-y-1.5">
                            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-cyan-400 border border-blue-200 dark:border-blue-500/20 text-xs font-bold cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-all">
                              <UploadCloud size={14} />
                              <span>Upload from PC (PNG, JPG, WEBP, SVG)</span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e, (dataUrl) => setNewProduct({ ...newProduct, iconUrl: dataUrl }))}
                              />
                            </label>
                            <input
                              type="text"
                              value={newProduct.iconUrl}
                              onChange={(e) => setNewProduct({ ...newProduct, iconUrl: e.target.value })}
                              placeholder="Or paste image URL (https://... or /assets/...)"
                              className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold text-slate-700 dark:text-slate-300">Product Title</label>
                        <input
                          type="text"
                          required
                          value={newProduct.title}
                          onChange={(e) => setNewProduct({ ...newProduct, title: e.target.value })}
                          placeholder="e.g. eCom By Hickter"
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold text-slate-700 dark:text-slate-300">Category Slug</label>
                          <input
                            type="text"
                            required
                            value={newProduct.category}
                            onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                            placeholder="ecommerce, crm, pos"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="font-bold text-slate-700 dark:text-slate-300">Custom Status Badge</label>
                          <input
                            type="text"
                            value={newProduct.customBadge}
                            onChange={(e) => setNewProduct({ ...newProduct, customBadge: e.target.value, status: e.target.value })}
                            placeholder="Google PlayStore Available"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold text-slate-700 dark:text-slate-300">Description</label>
                        <textarea
                          rows={2}
                          required
                          value={newProduct.description}
                          onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                          placeholder="Short summary of the product..."
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                        />
                      </div>

                      {/* Custom Links Section */}
                      <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-3">
                        <span className="font-extrabold text-[11px] uppercase tracking-wider text-slate-500 block">
                          Custom Links & Destination URLs
                        </span>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                              <Smartphone size={13} className="text-emerald-600 dark:text-emerald-400" />
                              <span>Google PlayStore Link</span>
                            </label>
                            <input
                              type="text"
                              value={newProduct.playStoreUrl}
                              onChange={(e) => setNewProduct({ ...newProduct, playStoreUrl: e.target.value })}
                              placeholder="https://play.google.com/store/apps/..."
                              className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                              <Globe size={13} className="text-blue-600 dark:text-blue-400" />
                              <span>Website / Live Demo Link</span>
                            </label>
                            <input
                              type="text"
                              value={newProduct.websiteUrl}
                              onChange={(e) => setNewProduct({ ...newProduct, websiteUrl: e.target.value })}
                              placeholder="https://ecom.hickter.com"
                              className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                              <Download size={13} className="text-purple-600 dark:text-purple-400" />
                              <span>Direct APK / File Download Link</span>
                            </label>
                            <input
                              type="text"
                              value={newProduct.apkUrl}
                              onChange={(e) => setNewProduct({ ...newProduct, apkUrl: e.target.value })}
                              placeholder="https://...download.apk"
                              className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                              <Eye size={13} className="text-cyan-600 dark:text-cyan-400" />
                              <span>Dedicated Detail Page Route</span>
                            </label>
                            <input
                              type="text"
                              value={newProduct.detailsUrl}
                              onChange={(e) => setNewProduct({ ...newProduct, detailsUrl: e.target.value })}
                              placeholder="/ourproducts/app/ecom"
                              className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Display & Button Visibility Toggles */}
                      <div className="p-3.5 rounded-2xl bg-blue-50/60 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 space-y-2">
                        <span className="font-extrabold text-[11px] uppercase tracking-wider text-blue-700 dark:text-cyan-400 block">
                          Card Display & Action Button Options
                        </span>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                          <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-800 dark:text-slate-200">
                            <input
                              type="checkbox"
                              checked={newProduct.isVisible}
                              onChange={(e) => setNewProduct({ ...newProduct, isVisible: e.target.checked })}
                              className="rounded cursor-pointer"
                            />
                            <span>Show on Site</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-800 dark:text-slate-200">
                            <input
                              type="checkbox"
                              checked={newProduct.showPlaystoreBtn}
                              onChange={(e) => setNewProduct({ ...newProduct, showPlaystoreBtn: e.target.checked })}
                              className="rounded cursor-pointer"
                            />
                            <span>PlayStore Btn</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-800 dark:text-slate-200">
                            <input
                              type="checkbox"
                              checked={newProduct.showWebsiteBtn}
                              onChange={(e) => setNewProduct({ ...newProduct, showWebsiteBtn: e.target.checked })}
                              className="rounded cursor-pointer"
                            />
                            <span>Website Btn</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-800 dark:text-slate-200">
                            <input
                              type="checkbox"
                              checked={newProduct.showDetailsBtn}
                              onChange={(e) => setNewProduct({ ...newProduct, showDetailsBtn: e.target.checked })}
                              className="rounded cursor-pointer"
                            />
                            <span>Detail Btn</span>
                          </label>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold text-slate-700 dark:text-slate-300">Highlight Stat Label</label>
                          <input
                            type="text"
                            value={newProduct.statLabel}
                            onChange={(e) => setNewProduct({ ...newProduct, statLabel: e.target.value })}
                            placeholder="Sync Engine"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="font-bold text-slate-700 dark:text-slate-300">Highlight Stat Value</label>
                          <input
                            type="text"
                            value={newProduct.statValue}
                            onChange={(e) => setNewProduct({ ...newProduct, statValue: e.target.value })}
                            placeholder="Real-Time Sync"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold text-slate-700 dark:text-slate-300">Technology Tags (Comma Separated)</label>
                        <input
                          type="text"
                          value={newProduct.tags}
                          onChange={(e) => setNewProduct({ ...newProduct, tags: e.target.value })}
                          placeholder="React, Next.js, Node.js"
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold text-slate-700 dark:text-slate-300">Features (Comma Separated)</label>
                        <input
                          type="text"
                          value={newProduct.features}
                          onChange={(e) => setNewProduct({ ...newProduct, features: e.target.value })}
                          placeholder="Feature 1, Feature 2, Feature 3"
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                        />
                      </div>

                      <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setIsAddProductOpen(false)}
                          className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 font-bold cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn-primary-blue px-5 py-2 rounded-xl font-bold !text-white cursor-pointer"
                        >
                          Create Product
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* EDIT PRODUCT MODAL */}
              {editingProduct && (
                <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                  <div className="w-full max-w-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                      <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                        Edit Product #{editingProduct.id}
                      </h3>
                      <button
                        onClick={() => setEditingProduct(null)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white cursor-pointer"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <form onSubmit={handleSaveProductEdit} className="space-y-4 text-xs">
                      {/* Image / Icon Uploader */}
                      <div className="space-y-1.5 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                        <label className="font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
                          <span>Product Icon / Brand Image</span>
                          <span className="text-[10px] text-blue-600 dark:text-cyan-400 font-semibold">Upload from PC or enter URL</span>
                        </label>
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-1 flex items-center justify-center shrink-0 overflow-hidden shadow-xs">
                            <img
                              src={editingProduct.iconUrl}
                              alt="Preview"
                              className="w-full h-full object-contain"
                              onError={(e: any) => { e.target.src = "/assets/logo/dl.webp"; }}
                            />
                          </div>
                          <div className="flex-1 space-y-1.5">
                            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-cyan-400 border border-blue-200 dark:border-blue-500/20 text-xs font-bold cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-all">
                              <UploadCloud size={14} />
                              <span>Change Image from PC</span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e, (dataUrl) => setEditingProduct({ ...editingProduct, iconUrl: dataUrl }))}
                              />
                            </label>
                            <input
                              type="text"
                              value={editingProduct.iconUrl}
                              onChange={(e) => setEditingProduct({ ...editingProduct, iconUrl: e.target.value })}
                              placeholder="Or paste image URL"
                              className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold text-slate-700 dark:text-slate-300">Product Title</label>
                        <input
                          type="text"
                          required
                          value={editingProduct.title}
                          onChange={(e) => setEditingProduct({ ...editingProduct, title: e.target.value })}
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold text-slate-700 dark:text-slate-300">Category Slug</label>
                          <input
                            type="text"
                            required
                            value={editingProduct.category}
                            onChange={(e) => setEditingProduct({ ...editingProduct, category: e.target.value })}
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="font-bold text-slate-700 dark:text-slate-300">Custom Status Badge</label>
                          <input
                            type="text"
                            value={editingProduct.customBadge || editingProduct.status}
                            onChange={(e) => setEditingProduct({ ...editingProduct, customBadge: e.target.value, status: e.target.value })}
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold text-slate-700 dark:text-slate-300">Description</label>
                        <textarea
                          rows={2}
                          required
                          value={editingProduct.description}
                          onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                        />
                      </div>

                      {/* Custom Links Section */}
                      <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-3">
                        <span className="font-extrabold text-[11px] uppercase tracking-wider text-slate-500 block">
                          Custom Links & Destination URLs
                        </span>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                              <Smartphone size={13} className="text-emerald-600 dark:text-emerald-400" />
                              <span>PlayStore URL</span>
                            </label>
                            <input
                              type="text"
                              value={editingProduct.playStoreUrl || ""}
                              onChange={(e) => setEditingProduct({ ...editingProduct, playStoreUrl: e.target.value })}
                              placeholder="https://play.google.com/store/apps/..."
                              className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                              <Globe size={13} className="text-blue-600 dark:text-blue-400" />
                              <span>Website / Demo URL</span>
                            </label>
                            <input
                              type="text"
                              value={editingProduct.websiteUrl || editingProduct.url}
                              onChange={(e) => setEditingProduct({ ...editingProduct, websiteUrl: e.target.value, url: e.target.value })}
                              placeholder="https://..."
                              className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                              <Download size={13} className="text-purple-600 dark:text-purple-400" />
                              <span>Direct APK / File Download Link</span>
                            </label>
                            <input
                              type="text"
                              value={editingProduct.apkUrl || ""}
                              onChange={(e) => setEditingProduct({ ...editingProduct, apkUrl: e.target.value })}
                              placeholder="https://...download.apk"
                              className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                              <Eye size={13} className="text-cyan-600 dark:text-cyan-400" />
                              <span>Dedicated Detail Route</span>
                            </label>
                            <input
                              type="text"
                              value={editingProduct.detailsUrl || ""}
                              onChange={(e) => setEditingProduct({ ...editingProduct, detailsUrl: e.target.value })}
                              placeholder="/ourproducts/app/ecom"
                              className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Display & Button Visibility Toggles */}
                      <div className="p-3.5 rounded-2xl bg-blue-50/60 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 space-y-2">
                        <span className="font-extrabold text-[11px] uppercase tracking-wider text-blue-700 dark:text-cyan-400 block">
                          Card Display & Action Button Options
                        </span>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                          <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-800 dark:text-slate-200">
                            <input
                              type="checkbox"
                              checked={editingProduct.isVisible !== false}
                              onChange={(e) => setEditingProduct({ ...editingProduct, isVisible: e.target.checked })}
                              className="rounded cursor-pointer"
                            />
                            <span>Show on Site</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-800 dark:text-slate-200">
                            <input
                              type="checkbox"
                              checked={editingProduct.showPlaystoreBtn !== false}
                              onChange={(e) => setEditingProduct({ ...editingProduct, showPlaystoreBtn: e.target.checked })}
                              className="rounded cursor-pointer"
                            />
                            <span>PlayStore Btn</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-800 dark:text-slate-200">
                            <input
                              type="checkbox"
                              checked={editingProduct.showWebsiteBtn !== false}
                              onChange={(e) => setEditingProduct({ ...editingProduct, showWebsiteBtn: e.target.checked })}
                              className="rounded cursor-pointer"
                            />
                            <span>Website Btn</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-800 dark:text-slate-200">
                            <input
                              type="checkbox"
                              checked={editingProduct.showDetailsBtn !== false}
                              onChange={(e) => setEditingProduct({ ...editingProduct, showDetailsBtn: e.target.checked })}
                              className="rounded cursor-pointer"
                            />
                            <span>Detail Btn</span>
                          </label>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold text-slate-700 dark:text-slate-300">Tags (Comma Separated)</label>
                        <input
                          type="text"
                          value={editingProduct.tags}
                          onChange={(e) => setEditingProduct({ ...editingProduct, tags: e.target.value })}
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold text-slate-700 dark:text-slate-300">Features (Comma Separated)</label>
                        <input
                          type="text"
                          value={editingProduct.features}
                          onChange={(e) => setEditingProduct({ ...editingProduct, features: e.target.value })}
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                        />
                      </div>

                      <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setEditingProduct(null)}
                          className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 font-bold cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn-primary-blue px-5 py-2 rounded-xl font-bold !text-white cursor-pointer"
                        >
                          Save Changes
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 3: PORTFOLIO MANAGER */}
          {/* ======================================================== */}
          {activeTab === "portfolio" && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                    Portfolio & Client Project Showcase
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Manage client deployments and custom categories. All updates sync directly with /Portfolio.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsAddPortfolioCatOpen(true)}
                    className="btn-outline py-2.5 px-3.5 text-xs font-bold rounded-xl flex items-center gap-1.5 cursor-pointer"
                  >
                    <FolderPlus size={15} />
                    <span>+ Add Category</span>
                  </button>
                  <button
                    onClick={() => setIsAddPortfolioOpen(true)}
                    className="btn-primary-blue py-2.5 px-4 text-xs font-bold rounded-xl flex items-center gap-2 !text-white shadow-md shadow-blue-500/20 cursor-pointer"
                  >
                    <Plus size={16} className="!text-white" />
                    <span className="!text-white">Add New Project</span>
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-800">
                <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
                  {distinctPortfolioCats.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setPortfolioCat(cat)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold uppercase transition-all cursor-pointer whitespace-nowrap ${
                        portfolioCat === cat
                          ? "bg-blue-600 dark:bg-blue-500 !text-white font-extrabold"
                          : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>

                <div className="relative w-full sm:w-64">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={portfolioSearch}
                    onChange={(e) => setPortfolioSearch(e.target.value)}
                    placeholder="Search projects..."
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl py-1.5 pl-8 pr-3 text-xs text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Portfolio Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredPortfolio.slice(0, 30).map((proj) => (
                  <div
                    key={proj.id}
                    className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs flex flex-col justify-between"
                  >
                    <div>
                      <div className="h-36 bg-slate-100 dark:bg-slate-950 relative overflow-hidden">
                        <img
                          src={proj.imageUrl}
                          alt={proj.title}
                          className="w-full h-full object-cover"
                          onError={(e: any) => {
                            e.target.onerror = null;
                            e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=600&q=80";
                          }}
                        />
                        <div className="absolute top-2 left-2 flex items-center gap-1.5">
                          <span className="px-2 py-0.5 rounded-md bg-black/70 backdrop-blur-md text-[10px] font-black uppercase text-white">
                            {proj.category}
                          </span>
                          {proj.isVisible === false && (
                            <span className="px-2 py-0.5 rounded-md bg-amber-500 text-[10px] font-black uppercase text-black">
                              Hidden
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="p-4 space-y-1.5">
                        <h4 className="text-sm font-extrabold text-slate-900 dark:text-white line-clamp-1">
                          {proj.title}
                        </h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                          {proj.descriptions}
                        </p>
                        <p className="text-[11px] font-mono text-blue-600 dark:text-cyan-400">
                          {proj.urldescriptions}
                        </p>
                      </div>
                    </div>

                    <div className="p-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/30">
                      <a
                        href={proj.url || "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-bold text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-cyan-400 flex items-center gap-1"
                      >
                        <span>Visit Site</span>
                        <ExternalLink size={11} />
                      </a>

                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => {
                            togglePortfolioVisibility(proj.id);
                            showToast(`Visibility updated for "${proj.title}"`);
                          }}
                          className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                            proj.isVisible !== false
                              ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                              : "bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400"
                          }`}
                          title={proj.isVisible !== false ? "Visible on Public Portfolio (Click to Hide)" : "Hidden from Public Portfolio (Click to Show)"}
                        >
                          {proj.isVisible !== false ? <Eye size={13} /> : <EyeOff size={13} />}
                        </button>
                        <button
                          onClick={() => setEditingPortfolio(proj)}
                          className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:text-blue-600 cursor-pointer"
                        >
                          <Edit3 size={13} />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete project "${proj.title}"?`)) {
                              deletePortfolio(proj.id);
                              showToast("Portfolio item deleted.");
                            }
                          }}
                          className="p-1.5 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 cursor-pointer"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ADD PORTFOLIO MODAL */}
              {isAddPortfolioOpen && (
                <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                  <div className="w-full max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                      <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                        Add New Portfolio Project
                      </h3>
                      <button onClick={() => setIsAddPortfolioOpen(false)} className="cursor-pointer">
                        <X size={18} />
                      </button>
                    </div>

                    <form onSubmit={handleCreatePortfolio} className="space-y-3 text-xs">
                      <div className="space-y-1">
                        <label className="font-bold">Project / Client Title</label>
                        <input
                          type="text"
                          required
                          value={newPortfolio.title}
                          onChange={(e) => setNewPortfolio({ ...newPortfolio, title: e.target.value })}
                          placeholder="e.g. Acme Health Portal"
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold">Category</label>
                          <select
                            value={newPortfolio.category}
                            onChange={(e) => setNewPortfolio({ ...newPortfolio, category: e.target.value })}
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-bold"
                          >
                            {distinctPortfolioCats.filter((c) => c !== "all").map((c) => (
                              <option key={c} value={c}>{c.toUpperCase()}</option>
                            ))}
                            <option value="custom">+ New Category...</option>
                          </select>
                        </div>

                        <div className="space-y-1">
                          <label className="font-bold">
                            {newPortfolio.category === "custom" ? "Type New Category" : "Domain Display"}
                          </label>
                          {newPortfolio.category === "custom" ? (
                            <input
                              type="text"
                              required
                              value={newPortfolio.customCategory}
                              onChange={(e) => setNewPortfolio({ ...newPortfolio, customCategory: e.target.value })}
                              placeholder="e.g. fintech, ai-ml"
                              className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-blue-400 dark:border-blue-500 font-bold text-blue-600 dark:text-cyan-400"
                            />
                          ) : (
                            <input
                              type="text"
                              value={newPortfolio.urldescriptions}
                              onChange={(e) => setNewPortfolio({ ...newPortfolio, urldescriptions: e.target.value })}
                              placeholder="acmehealth.com"
                              className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                            />
                          )}
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold">Description</label>
                        <textarea
                          rows={2}
                          required
                          value={newPortfolio.descriptions}
                          onChange={(e) => setNewPortfolio({ ...newPortfolio, descriptions: e.target.value })}
                          placeholder="Brief description of the delivered solution..."
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                        />
                      </div>

                      {/* Image Uploader */}
                      <div className="space-y-1.5 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                        <label className="font-bold flex items-center justify-between">
                          <span>Project Showcase Image</span>
                          <span className="text-[10px] text-blue-600 dark:text-cyan-400 font-semibold">Upload from PC or enter URL</span>
                        </label>
                        <div className="flex items-center gap-3">
                          <div className="w-16 h-12 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 overflow-hidden shrink-0 shadow-xs">
                            <img
                              src={newPortfolio.imageUrl}
                              alt="Preview"
                              className="w-full h-full object-cover"
                              onError={(e: any) => { e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=600&q=80"; }}
                            />
                          </div>
                          <div className="flex-1 space-y-1.5">
                            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-500/20 text-xs font-bold cursor-pointer hover:bg-purple-100 transition-all">
                              <UploadCloud size={14} />
                              <span>Upload Screenshot from PC</span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e, (dataUrl) => setNewPortfolio({ ...newPortfolio, imageUrl: dataUrl }))}
                              />
                            </label>
                            <input
                              type="text"
                              value={newPortfolio.imageUrl}
                              onChange={(e) => setNewPortfolio({ ...newPortfolio, imageUrl: e.target.value })}
                              placeholder="/assets/portfolio/... or https://..."
                              className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold">Live Target Project Link</label>
                        <input
                          type="text"
                          value={newPortfolio.url}
                          onChange={(e) => setNewPortfolio({ ...newPortfolio, url: e.target.value })}
                          placeholder="https://example.com"
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                        />
                      </div>

                      <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setIsAddPortfolioOpen(false)}
                          className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 font-bold"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn-primary-blue px-5 py-2 rounded-xl font-bold !text-white"
                        >
                          Add Project
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* ADD PORTFOLIO CATEGORY MODAL */}
              {isAddPortfolioCatOpen && (
                <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                  <div className="w-full max-w-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                      <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                        <FolderPlus size={18} className="text-blue-600 dark:text-cyan-400" />
                        <span>Add New Category</span>
                      </h3>
                      <button onClick={() => setIsAddPortfolioCatOpen(false)} className="cursor-pointer">
                        <X size={18} />
                      </button>
                    </div>

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (newPortfolioCatInput.trim()) {
                          addPortfolioCategory(newPortfolioCatInput.trim().toLowerCase());
                          setPortfolioCat(newPortfolioCatInput.trim().toLowerCase());
                          setNewPortfolioCatInput("");
                          setIsAddPortfolioCatOpen(false);
                          showToast("New category created successfully!");
                        }
                      }}
                      className="space-y-4 text-xs"
                    >
                      <div className="space-y-1">
                        <label className="font-bold">Category Identifier / Name</label>
                        <input
                          type="text"
                          required
                          value={newPortfolioCatInput}
                          onChange={(e) => setNewPortfolioCatInput(e.target.value)}
                          placeholder="e.g. artificial-intelligence, fintech"
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-bold"
                        />
                        <p className="text-[10px] text-slate-400">
                          This creates a new filter pill in the Portfolio showcase and category switcher.
                        </p>
                      </div>

                      <div className="pt-2 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setIsAddPortfolioCatOpen(false)}
                          className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 font-bold cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn-primary-blue px-5 py-2 rounded-xl font-bold !text-white cursor-pointer"
                        >
                          Create Category
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* EDIT PORTFOLIO MODAL */}
              {editingPortfolio && (
                <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                  <div className="w-full max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                      <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                        Edit Portfolio #{editingPortfolio.id}
                      </h3>
                      <button onClick={() => setEditingPortfolio(null)} className="cursor-pointer">
                        <X size={18} />
                      </button>
                    </div>

                    <form onSubmit={handleSavePortfolioEdit} className="space-y-3 text-xs">
                      <div className="space-y-1">
                        <label className="font-bold">Title</label>
                        <input
                          type="text"
                          required
                          value={editingPortfolio.title}
                          onChange={(e) => setEditingPortfolio({ ...editingPortfolio, title: e.target.value })}
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold">Category</label>
                          <input
                            type="text"
                            value={editingPortfolio.category}
                            onChange={(e) => setEditingPortfolio({ ...editingPortfolio, category: e.target.value })}
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="font-bold">Domain Display</label>
                          <input
                            type="text"
                            value={editingPortfolio.urldescriptions}
                            onChange={(e) => setEditingPortfolio({ ...editingPortfolio, urldescriptions: e.target.value })}
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold">Description</label>
                        <textarea
                          rows={2}
                          value={editingPortfolio.descriptions}
                          onChange={(e) => setEditingPortfolio({ ...editingPortfolio, descriptions: e.target.value })}
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                        />
                      </div>

                      {/* Image Uploader */}
                      <div className="space-y-1.5 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                        <label className="font-bold flex items-center justify-between">
                          <span>Project Showcase Image</span>
                          <span className="text-[10px] text-blue-600 dark:text-cyan-400 font-semibold">Upload from PC or enter URL</span>
                        </label>
                        <div className="flex items-center gap-3">
                          <div className="w-16 h-12 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 overflow-hidden shrink-0 shadow-xs">
                            <img
                              src={editingPortfolio.imageUrl}
                              alt="Preview"
                              className="w-full h-full object-cover"
                              onError={(e: any) => { e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=600&q=80"; }}
                            />
                          </div>
                          <div className="flex-1 space-y-1.5">
                            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-500/20 text-xs font-bold cursor-pointer hover:bg-purple-100 transition-all">
                              <UploadCloud size={14} />
                              <span>Change Image from PC</span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e, (dataUrl) => setEditingPortfolio({ ...editingPortfolio, imageUrl: dataUrl }))}
                              />
                            </label>
                            <input
                              type="text"
                              value={editingPortfolio.imageUrl}
                              onChange={(e) => setEditingPortfolio({ ...editingPortfolio, imageUrl: e.target.value })}
                              placeholder="Image URL"
                              className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold">Live Target Link</label>
                        <input
                          type="text"
                          value={editingPortfolio.url || ""}
                          onChange={(e) => setEditingPortfolio({ ...editingPortfolio, url: e.target.value })}
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                        />
                      </div>

                      <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setEditingPortfolio(null)}
                          className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 font-bold"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn-primary-blue px-5 py-2 rounded-xl font-bold !text-white"
                        >
                          Save Changes
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 4: PRICING & PLANS */}
          {/* ======================================================== */}
          {activeTab === "pricing" && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                    Pricing Plans & Packages
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Manage prices, features, custom categories, icons/images, and direct apply links across all plans.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsAddPricingCatOpen(true)}
                    className="btn-outline py-2.5 px-3.5 text-xs font-bold rounded-xl flex items-center gap-1.5 cursor-pointer"
                  >
                    <FolderPlus size={15} />
                    <span>+ Add Category</span>
                  </button>
                  <button
                    onClick={() => setIsAddPlanOpen(true)}
                    className="btn-primary-blue py-2.5 px-4 text-xs font-bold rounded-xl flex items-center gap-2 !text-white shadow-md shadow-blue-500/20 cursor-pointer"
                  >
                    <Plus size={16} className="!text-white" />
                    <span className="!text-white">Add New Plan</span>
                  </button>
                </div>
              </div>

              {/* Category Switcher */}
              <div className="flex items-center gap-1.5 bg-white dark:bg-slate-900 p-1.5 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-x-auto">
                {distinctPricingCats.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setPricingCat(cat)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold uppercase transition-all cursor-pointer whitespace-nowrap ${
                      pricingCat === cat
                        ? "bg-blue-600 dark:bg-blue-500 !text-white font-extrabold"
                        : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                    }`}
                  >
                    {cat === "all" ? "All Plans" : cat === "web" ? "Web Services" : cat === "app" ? "App Development" : cat === "package" ? "Turnkey Packages" : cat.toUpperCase()}
                  </button>
                ))}
              </div>

              {/* Plans Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredPricing.map((plan) => (
                  <div
                    key={plan.id}
                    className={`p-6 rounded-3xl bg-white dark:bg-slate-900 border ${
                      plan.isVisible === false
                        ? "border-amber-400/50 opacity-70 bg-amber-50/10"
                        : plan.popular
                        ? "border-blue-500 dark:border-blue-400 ring-2 ring-blue-500/20"
                        : "border-slate-200 dark:border-slate-800"
                    } shadow-sm flex flex-col justify-between space-y-5`}
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                          {plan.category || "web"}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {plan.isVisible === false && (
                            <span className="px-2 py-0.5 rounded-full bg-amber-500 text-[10px] font-black uppercase text-black">
                              Hidden
                            </span>
                          )}
                          {(plan.customBadge || plan.popular) && (
                            <span className="px-2.5 py-0.5 rounded-full bg-blue-600 !text-white font-black text-[10px] uppercase">
                              {plan.customBadge || "Most Popular"}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Image / Icon Preview if Available */}
                      {plan.imageUrl && (
                        <div className="w-12 h-12 rounded-2xl overflow-hidden bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xs">
                          <img src={plan.imageUrl} alt={plan.title} className="w-full h-full object-cover" />
                        </div>
                      )}

                      <div>
                        <h3 className="text-lg font-black text-slate-900 dark:text-white">
                          {plan.title}
                        </h3>
                        <p className="text-xs text-slate-400 font-medium">
                          {plan.subTitle}
                        </p>
                      </div>

                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-black text-slate-900 dark:text-white">
                          ₹{plan.inrPrice}
                        </span>
                        <span className="text-xs text-slate-400 font-semibold">
                          / ${plan.usdPrice} USD
                        </span>
                        {plan.billingCycle && (
                          <span className="text-[11px] text-blue-600 dark:text-cyan-400 font-bold ml-auto">
                            {plan.billingCycle}
                          </span>
                        )}
                      </div>

                      {/* Features */}
                      <div className="space-y-1.5 pt-2 border-t border-slate-100 dark:border-slate-800">
                        {(plan.features || []).map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                            <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />
                            <span className="line-clamp-1">{f}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                      <span className="text-[11px] text-slate-400 font-mono truncate max-w-[120px]">
                        {plan.link}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => {
                            togglePricingVisibility(plan.id);
                            showToast(`Visibility updated for "${plan.title}"`);
                          }}
                          className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                            plan.isVisible !== false
                              ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                              : "bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400"
                          }`}
                          title={plan.isVisible !== false ? "Visible on Public Pricing (Click to Hide)" : "Hidden from Public Pricing (Click to Show)"}
                        >
                          {plan.isVisible !== false ? <Eye size={13} /> : <EyeOff size={13} />}
                        </button>
                        <button
                          onClick={() => setEditingPlan(plan)}
                          className="btn-outline px-2.5 py-1 text-xs font-bold rounded-xl flex items-center gap-1 cursor-pointer"
                        >
                          <Edit3 size={13} />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete pricing plan "${plan.title}"?`)) {
                              deletePricingPlan(plan.id);
                              showToast("Pricing plan deleted.");
                            }
                          }}
                          className="p-1.5 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 cursor-pointer"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ADD PRICING PLAN MODAL */}
              {isAddPlanOpen && (
                <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                  <div className="w-full max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                      <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                        <Plus size={18} className="text-blue-600 dark:text-cyan-400" />
                        <span>Add New Pricing Plan</span>
                      </h3>
                      <button onClick={() => setIsAddPlanOpen(false)} className="cursor-pointer">
                        <X size={18} />
                      </button>
                    </div>

                    <form onSubmit={handleCreatePlan} className="space-y-3 text-xs">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold">Plan Title</label>
                          <input
                            type="text"
                            required
                            value={newPlan.title}
                            onChange={(e) => setNewPlan({ ...newPlan, title: e.target.value })}
                            placeholder="e.g. Pro Growth Plan"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-bold"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="font-bold">Subtitle / Tagline</label>
                          <input
                            type="text"
                            value={newPlan.subTitle}
                            onChange={(e) => setNewPlan({ ...newPlan, subTitle: e.target.value })}
                            placeholder="e.g. Next-Gen Full Stack"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold">Category</label>
                          <select
                            value={newPlan.category}
                            onChange={(e) => setNewPlan({ ...newPlan, category: e.target.value })}
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-bold"
                          >
                            {distinctPricingCats.filter((c) => c !== "all").map((c) => (
                              <option key={c} value={c}>{c.toUpperCase()}</option>
                            ))}
                            <option value="custom">+ New Category...</option>
                          </select>
                        </div>

                        <div className="space-y-1">
                          <label className="font-bold">
                            {newPlan.category === "custom" ? "Type New Category" : "Billing Cycle"}
                          </label>
                          {newPlan.category === "custom" ? (
                            <input
                              type="text"
                              required
                              value={newPlan.customCategory}
                              onChange={(e) => setNewPlan({ ...newPlan, customCategory: e.target.value })}
                              placeholder="e.g. saas, ecommerce, retainer"
                              className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-blue-400 dark:border-blue-500 font-bold text-blue-600 dark:text-cyan-400"
                            />
                          ) : (
                            <input
                              type="text"
                              value={newPlan.billingCycle}
                              onChange={(e) => setNewPlan({ ...newPlan, billingCycle: e.target.value })}
                              placeholder="One-time / Monthly / Annual"
                              className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                            />
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold">INR Price (₹)</label>
                          <input
                            type="text"
                            required
                            value={newPlan.inrPrice}
                            onChange={(e) => setNewPlan({ ...newPlan, inrPrice: e.target.value })}
                            placeholder="e.g. 24,999"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-bold"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="font-bold">USD Price ($)</label>
                          <input
                            type="text"
                            required
                            value={newPlan.usdPrice}
                            onChange={(e) => setNewPlan({ ...newPlan, usdPrice: e.target.value })}
                            placeholder="e.g. 349"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-bold"
                          />
                        </div>
                      </div>

                      {/* Image / Icon Uploader */}
                      <div className="space-y-1.5 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                        <label className="font-bold flex items-center justify-between">
                          <span>Plan Icon / Image (Optional)</span>
                          <span className="text-[10px] text-blue-600 dark:text-cyan-400 font-semibold">Upload from PC or enter URL</span>
                        </label>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 overflow-hidden shrink-0 shadow-xs flex items-center justify-center">
                            {newPlan.imageUrl ? (
                              <img src={newPlan.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                            ) : (
                              <ImageIcon size={20} className="text-slate-400" />
                            )}
                          </div>
                          <div className="flex-1 space-y-1.5">
                            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-cyan-400 border border-blue-200 dark:border-blue-500/20 text-xs font-bold cursor-pointer hover:bg-blue-100 transition-all">
                              <UploadCloud size={14} />
                              <span>Upload Plan Image from PC</span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e, (dataUrl) => setNewPlan({ ...newPlan, imageUrl: dataUrl }))}
                              />
                            </label>
                            <input
                              type="text"
                              value={newPlan.imageUrl}
                              onChange={(e) => setNewPlan({ ...newPlan, imageUrl: e.target.value })}
                              placeholder="/assets/... or https://..."
                              className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold">Apply Link / Route</label>
                          <input
                            type="text"
                            value={newPlan.link}
                            onChange={(e) => setNewPlan({ ...newPlan, link: e.target.value })}
                            placeholder="/apply/package or https://..."
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="font-bold">CTA Button Text</label>
                          <input
                            type="text"
                            value={newPlan.buttonText}
                            onChange={(e) => setNewPlan({ ...newPlan, buttonText: e.target.value })}
                            placeholder="e.g. Choose Plan"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold">Features (Comma Separated)</label>
                        <textarea
                          rows={3}
                          value={newPlan.features}
                          onChange={(e) => setNewPlan({ ...newPlan, features: e.target.value })}
                          placeholder="Feature 1, Feature 2, Feature 3, 24/7 SLA"
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3 pt-1">
                        <div className="space-y-1">
                          <label className="font-bold">Custom Badge Text</label>
                          <input
                            type="text"
                            value={newPlan.customBadge}
                            onChange={(e) => setNewPlan({ ...newPlan, customBadge: e.target.value })}
                            placeholder="e.g. Most Popular / Best Value"
                            className="w-full p-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                        <div className="flex items-center gap-2 pt-5">
                          <input
                            type="checkbox"
                            id="newPopularCheck"
                            checked={newPlan.popular}
                            onChange={(e) => setNewPlan({ ...newPlan, popular: e.target.checked })}
                            className="w-4 h-4 rounded text-blue-600"
                          />
                          <label htmlFor="newPopularCheck" className="font-bold cursor-pointer">
                            Highlight Plan
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-1">
                        <input
                          type="checkbox"
                          id="newPlanVisibleCheck"
                          checked={newPlan.isVisible}
                          onChange={(e) => setNewPlan({ ...newPlan, isVisible: e.target.checked })}
                          className="w-4 h-4 rounded text-blue-600"
                        />
                        <label htmlFor="newPlanVisibleCheck" className="font-bold cursor-pointer">
                          Visible on Public /Pricing Page
                        </label>
                      </div>

                      <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setIsAddPlanOpen(false)}
                          className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 font-bold cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn-primary-blue px-5 py-2 rounded-xl font-bold !text-white cursor-pointer"
                        >
                          Create Plan
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* ADD PRICING CATEGORY MODAL */}
              {isAddPricingCatOpen && (
                <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                  <div className="w-full max-w-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                      <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                        <FolderPlus size={18} className="text-blue-600 dark:text-cyan-400" />
                        <span>Add Pricing Category</span>
                      </h3>
                      <button onClick={() => setIsAddPricingCatOpen(false)} className="cursor-pointer">
                        <X size={18} />
                      </button>
                    </div>

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (newPricingCatInput.trim()) {
                          addPricingCategory(newPricingCatInput.trim().toLowerCase());
                          setPricingCat(newPricingCatInput.trim().toLowerCase());
                          setNewPricingCatInput("");
                          setIsAddPricingCatOpen(false);
                          showToast("New pricing category created!");
                        }
                      }}
                      className="space-y-4 text-xs"
                    >
                      <div className="space-y-1">
                        <label className="font-bold">Category Name / Identifier</label>
                        <input
                          type="text"
                          required
                          value={newPricingCatInput}
                          onChange={(e) => setNewPricingCatInput(e.target.value)}
                          placeholder="e.g. saas, ecommerce, enterprise"
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-bold"
                        />
                      </div>

                      <div className="pt-2 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setIsAddPricingCatOpen(false)}
                          className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 font-bold cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn-primary-blue px-5 py-2 rounded-xl font-bold !text-white cursor-pointer"
                        >
                          Add Category
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* EDIT PRICING MODAL */}
              {editingPlan && (
                <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                  <div className="w-full max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                      <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                        Edit Pricing Plan: {editingPlan.title}
                      </h3>
                      <button onClick={() => setEditingPlan(null)} className="cursor-pointer">
                        <X size={18} />
                      </button>
                    </div>

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        updatePricingPlan(editingPlan.id, editingPlan);
                        setEditingPlan(null);
                        showToast(`Plan ${editingPlan.title} updated!`);
                      }}
                      className="space-y-3 text-xs"
                    >
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold">Plan Title</label>
                          <input
                            type="text"
                            required
                            value={editingPlan.title}
                            onChange={(e) => setEditingPlan({ ...editingPlan, title: e.target.value })}
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-bold"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="font-bold">Subtitle</label>
                          <input
                            type="text"
                            value={editingPlan.subTitle}
                            onChange={(e) => setEditingPlan({ ...editingPlan, subTitle: e.target.value })}
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold">Category</label>
                          <input
                            type="text"
                            value={editingPlan.category || "web"}
                            onChange={(e) => setEditingPlan({ ...editingPlan, category: e.target.value })}
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="font-bold">Billing Cycle</label>
                          <input
                            type="text"
                            value={editingPlan.billingCycle || "One-time"}
                            onChange={(e) => setEditingPlan({ ...editingPlan, billingCycle: e.target.value })}
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold">INR Price (₹)</label>
                          <input
                            type="text"
                            required
                            value={editingPlan.inrPrice}
                            onChange={(e) => setEditingPlan({ ...editingPlan, inrPrice: e.target.value })}
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-bold"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="font-bold">USD Price ($)</label>
                          <input
                            type="text"
                            required
                            value={editingPlan.usdPrice}
                            onChange={(e) => setEditingPlan({ ...editingPlan, usdPrice: e.target.value })}
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-bold"
                          />
                        </div>
                      </div>

                      {/* Image / Icon Uploader */}
                      <div className="space-y-1.5 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                        <label className="font-bold flex items-center justify-between">
                          <span>Plan Icon / Image</span>
                          <span className="text-[10px] text-blue-600 dark:text-cyan-400 font-semibold">Upload from PC or enter URL</span>
                        </label>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 overflow-hidden shrink-0 shadow-xs flex items-center justify-center">
                            {editingPlan.imageUrl ? (
                              <img src={editingPlan.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                            ) : (
                              <ImageIcon size={20} className="text-slate-400" />
                            )}
                          </div>
                          <div className="flex-1 space-y-1.5">
                            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-cyan-400 border border-blue-200 dark:border-blue-500/20 text-xs font-bold cursor-pointer hover:bg-blue-100 transition-all">
                              <UploadCloud size={14} />
                              <span>Change Image from PC</span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e, (dataUrl) => setEditingPlan({ ...editingPlan, imageUrl: dataUrl }))}
                              />
                            </label>
                            <input
                              type="text"
                              value={editingPlan.imageUrl || ""}
                              onChange={(e) => setEditingPlan({ ...editingPlan, imageUrl: e.target.value })}
                              placeholder="/assets/... or https://..."
                              className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold">Apply Link URL</label>
                          <input
                            type="text"
                            value={editingPlan.link}
                            onChange={(e) => setEditingPlan({ ...editingPlan, link: e.target.value })}
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="font-bold">Button Text</label>
                          <input
                            type="text"
                            value={editingPlan.buttonText || ""}
                            onChange={(e) => setEditingPlan({ ...editingPlan, buttonText: e.target.value })}
                            placeholder="e.g. Choose Plan"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold">Features (Comma Separated)</label>
                        <textarea
                          rows={3}
                          value={editingPlan.features.join(", ")}
                          onChange={(e) =>
                            setEditingPlan({
                              ...editingPlan,
                              features: e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
                            })
                          }
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3 pt-1">
                        <div className="space-y-1">
                          <label className="font-bold">Custom Badge Text</label>
                          <input
                            type="text"
                            value={editingPlan.customBadge || ""}
                            onChange={(e) => setEditingPlan({ ...editingPlan, customBadge: e.target.value })}
                            placeholder="e.g. Most Popular / Best Value"
                            className="w-full p-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                        <div className="flex items-center gap-2 pt-5">
                          <input
                            type="checkbox"
                            id="editPopularCheck"
                            checked={!!editingPlan.popular}
                            onChange={(e) => setEditingPlan({ ...editingPlan, popular: e.target.checked })}
                            className="w-4 h-4 rounded text-blue-600"
                          />
                          <label htmlFor="editPopularCheck" className="font-bold cursor-pointer">
                            Highlight Plan
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-1">
                        <input
                          type="checkbox"
                          id="editPlanVisibleCheck"
                          checked={editingPlan.isVisible !== false}
                          onChange={(e) => setEditingPlan({ ...editingPlan, isVisible: e.target.checked })}
                          className="w-4 h-4 rounded text-blue-600"
                        />
                        <label htmlFor="editPlanVisibleCheck" className="font-bold cursor-pointer">
                          Visible on Public /Pricing Page
                        </label>
                      </div>

                      <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setEditingPlan(null)}
                          className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 font-bold cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn-primary-blue px-5 py-2 rounded-xl font-bold !text-white cursor-pointer"
                        >
                          Save Plan
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 5: HOME PAGE BUILDER & SECTION REORDER */}
          {/* ======================================================== */}
          {activeTab === "home" && (
            <div className="space-y-8 max-w-4xl">
              <div>
                <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                  Home Page Builder & Section Reordering
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Drag and drop sections or use Up/Down arrows to reorder how sections appear on the homepage. Edit titles, descriptions, buttons, or toggle visibility.
                </p>
              </div>

              {/* DYNAMIC SECTIONS LIST WITH DRAGGING & REORDERING */}
              <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                  <div>
                    <h3 className="text-sm font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                      <Layers size={16} className="text-blue-600 dark:text-cyan-400" />
                      <span>Home Sections Layout & Display Order</span>
                    </h3>
                    <p className="text-[11px] text-slate-400">
                      Sections render on the homepage in this exact numerical sequence. Drag with handle or click arrows.
                    </p>
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-cyan-400">
                    {(homeSections || []).length} Sections
                  </span>
                </div>

                <div className="space-y-2.5">
                  {(homeSections || []).map((sec, idx) => (
                    <div
                      key={sec.id}
                      draggable
                      onDragStart={() => setDraggedSectionIndex(idx)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (draggedSectionIndex !== null && draggedSectionIndex !== idx) {
                          reorderHomeSection(draggedSectionIndex, idx);
                          setDraggedSectionIndex(null);
                          showToast(`Reordered section "${sec.name}"`);
                        }
                      }}
                      className={`p-3.5 rounded-2xl bg-slate-50/70 dark:bg-slate-800/50 border ${
                        sec.isVisible === false
                          ? "border-amber-400/40 opacity-70 bg-amber-50/10"
                          : "border-slate-200 dark:border-slate-700/60"
                      } shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-all hover:border-blue-500/50 cursor-grab active:cursor-grabbing`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-grab shrink-0">
                          <GripVertical size={16} />
                        </div>
                        <span className="w-7 h-7 rounded-xl bg-blue-600 !text-white font-black text-xs flex items-center justify-center shrink-0 shadow-xs">
                          #{idx + 1}
                        </span>
                        <div>
                          <h4 className="text-xs sm:text-sm font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                            <span>{sec.name}</span>
                            {sec.isVisible === false && (
                              <span className="px-2 py-0.5 rounded-full bg-amber-500 text-[9px] font-black uppercase text-black">
                                Hidden
                              </span>
                            )}
                          </h4>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium line-clamp-1">
                            {sec.title || sec.subtitle || `Identifier: ${sec.id}`}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 self-end sm:self-center shrink-0">
                        <button
                          type="button"
                          disabled={idx === 0}
                          onClick={() => moveSection(idx, "up")}
                          className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                          title="Move Up"
                        >
                          <ArrowUp size={14} />
                        </button>
                        <button
                          type="button"
                          disabled={idx === (homeSections || []).length - 1}
                          onClick={() => moveSection(idx, "down")}
                          className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                          title="Move Down"
                        >
                          <ArrowDown size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            toggleHomeSectionVisibility(sec.id);
                            showToast(`Toggled visibility for ${sec.name}`);
                          }}
                          className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                            sec.isVisible !== false
                              ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                              : "bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400"
                          }`}
                          title={sec.isVisible !== false ? "Visible on Home (Click to Hide)" : "Hidden from Home (Click to Show)"}
                        >
                          {sec.isVisible !== false ? <Eye size={14} /> : <EyeOff size={14} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingSection(sec)}
                          className="btn-outline px-3 py-1.5 text-xs font-bold rounded-xl flex items-center gap-1 cursor-pointer"
                        >
                          <Edit3 size={13} />
                          <span>Edit Content</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* EDIT SECTION CONTENT MODAL */}
              {editingSection && (
                <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                  <div className="w-full max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                      <div>
                        <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                          Edit Section: {editingSection.name}
                        </h3>
                        <p className="text-[10px] text-slate-400 font-mono">
                          ID: #{editingSection.id} | Position: Order #{editingSection.order}
                        </p>
                      </div>
                      <button onClick={() => setEditingSection(null)} className="cursor-pointer">
                        <X size={18} />
                      </button>
                    </div>

                    <form onSubmit={handleSaveSectionEdit} className="space-y-3 text-xs">
                      <div className="space-y-1">
                        <label className="font-bold">Badge Text</label>
                        <input
                          type="text"
                          value={editingSection.badge || ""}
                          onChange={(e) => setEditingSection({ ...editingSection, badge: e.target.value })}
                          placeholder="e.g. Engineering Capabilities / Client Showcase"
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold">Section Main Headline / Title</label>
                        <input
                          type="text"
                          required
                          value={editingSection.title || ""}
                          onChange={(e) => setEditingSection({ ...editingSection, title: e.target.value })}
                          placeholder="Headline text..."
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-bold"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold">Subtitle / Description</label>
                        <textarea
                          rows={3}
                          value={editingSection.subtitle || ""}
                          onChange={(e) => setEditingSection({ ...editingSection, subtitle: e.target.value })}
                          placeholder="Supporting paragraph copy..."
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold">Primary CTA Text</label>
                          <input
                            type="text"
                            value={editingSection.ctaText || ""}
                            onChange={(e) => setEditingSection({ ...editingSection, ctaText: e.target.value })}
                            placeholder="e.g. View All Projects"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="font-bold">Primary CTA URL</label>
                          <input
                            type="text"
                            value={editingSection.ctaUrl || ""}
                            onChange={(e) => setEditingSection({ ...editingSection, ctaUrl: e.target.value })}
                            placeholder="/Portfolio"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold">Secondary CTA Text</label>
                          <input
                            type="text"
                            value={editingSection.secondaryCtaText || ""}
                            onChange={(e) => setEditingSection({ ...editingSection, secondaryCtaText: e.target.value })}
                            placeholder="e.g. Schedule Consultation"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="font-bold">Secondary CTA URL</label>
                          <input
                            type="text"
                            value={editingSection.secondaryCtaUrl || ""}
                            onChange={(e) => setEditingSection({ ...editingSection, secondaryCtaUrl: e.target.value })}
                            placeholder="/Meeting"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-2">
                        <input
                          type="checkbox"
                          id="editSecVisibleCheck"
                          checked={editingSection.isVisible !== false}
                          onChange={(e) => setEditingSection({ ...editingSection, isVisible: e.target.checked })}
                          className="w-4 h-4 rounded text-blue-600"
                        />
                        <label htmlFor="editSecVisibleCheck" className="font-bold cursor-pointer">
                          Visible on Public Homepage
                        </label>
                      </div>

                      <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setEditingSection(null)}
                          className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 font-bold cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn-primary-blue px-5 py-2 rounded-xl font-bold !text-white cursor-pointer"
                        >
                          Save Section
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* GLOBAL HERO & METRICS SETTINGS FORM */}
              <form onSubmit={handleSaveHome} className="space-y-5 bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm text-xs">
                <div className="border-b border-slate-100 dark:border-slate-800 pb-2">
                  <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">
                    Global Hero Banner, Live Metrics & Top Announcement
                  </h3>
                </div>

                {/* Hero Headline */}
                <div className="space-y-1.5">
                  <label className="font-bold text-slate-700 dark:text-slate-300">Hero Main Headline</label>
                  <input
                    type="text"
                    required
                    value={homeForm.heroHeadline}
                    onChange={(e) => setHomeForm({ ...homeForm, heroHeadline: e.target.value })}
                    className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-bold text-sm"
                  />
                </div>

                {/* Hero Subheadline */}
                <div className="space-y-1.5">
                  <label className="font-bold text-slate-700 dark:text-slate-300">Hero Subheadline / Value Proposition</label>
                  <textarea
                    rows={3}
                    required
                    value={homeForm.heroSubheadline}
                    onChange={(e) => setHomeForm({ ...homeForm, heroSubheadline: e.target.value })}
                    className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                  />
                </div>

                {/* CTAs */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="font-bold text-slate-700 dark:text-slate-300">Primary CTA Text</label>
                    <input
                      type="text"
                      value={homeForm.heroPrimaryCtaText}
                      onChange={(e) => setHomeForm({ ...homeForm, heroPrimaryCtaText: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="font-bold text-slate-700 dark:text-slate-300">Primary CTA URL</label>
                    <input
                      type="text"
                      value={homeForm.heroPrimaryCtaUrl}
                      onChange={(e) => setHomeForm({ ...homeForm, heroPrimaryCtaUrl: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                    />
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                  <div className="space-y-1">
                    <label className="font-bold">Clients Count</label>
                    <input
                      type="text"
                      value={homeForm.statsClients}
                      onChange={(e) => setHomeForm({ ...homeForm, statsClients: e.target.value })}
                      className="w-full p-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="font-bold">Projects Delivered</label>
                    <input
                      type="text"
                      value={homeForm.statsProjects}
                      onChange={(e) => setHomeForm({ ...homeForm, statsProjects: e.target.value })}
                      className="w-full p-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="font-bold">Years Experience</label>
                    <input
                      type="text"
                      value={homeForm.statsExperience}
                      onChange={(e) => setHomeForm({ ...homeForm, statsExperience: e.target.value })}
                      className="w-full p-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="font-bold">Uptime SLA</label>
                    <input
                      type="text"
                      value={homeForm.statsUptime}
                      onChange={(e) => setHomeForm({ ...homeForm, statsUptime: e.target.value })}
                      className="w-full p-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                    />
                  </div>
                </div>

                {/* Announcement Bar */}
                <div className="p-4 rounded-2xl bg-blue-50/50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-extrabold text-blue-900 dark:text-cyan-300">
                      Top Announcement Bar
                    </span>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={homeForm.announcementActive}
                        onChange={(e) => setHomeForm({ ...homeForm, announcementActive: e.target.checked })}
                        className="w-4 h-4 rounded text-blue-600"
                      />
                      <span className="font-bold text-xs">Active on Site</span>
                    </label>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={homeForm.announcementText}
                      onChange={(e) => setHomeForm({ ...homeForm, announcementText: e.target.value })}
                      placeholder="Announcement message text..."
                      className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700"
                    />
                    <input
                      type="text"
                      value={homeForm.announcementUrl}
                      onChange={(e) => setHomeForm({ ...homeForm, announcementUrl: e.target.value })}
                      placeholder="/link-target"
                      className="w-full p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700"
                    />
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex justify-end">
                  <button
                    type="submit"
                    className="btn-primary-blue px-6 py-2.5 rounded-xl font-bold !text-white flex items-center gap-2 cursor-pointer shadow-md"
                  >
                    <Save size={15} className="!text-white" />
                    <span className="!text-white">Save Home Configuration</span>
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB: SITE STYLING, TYPOGRAPHY & THEME */}
          {/* ======================================================== */}
          {activeTab === "theme" && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                    Site Styling, Typography & Theme Customizer
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Customize Hickter's global brand color, accent color, fonts, border radius, and branding. Changes update across all pages in real time.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleResetTheme}
                    className="p-2.5 px-4 rounded-xl border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer flex items-center gap-1.5"
                  >
                    <RotateCcw size={14} />
                    <span>Reset Defaults</span>
                  </button>
                  <button
                    onClick={handleSaveTheme}
                    className="btn-primary-blue py-2.5 px-5 text-xs font-bold rounded-xl flex items-center gap-2 !text-white shadow-md shadow-blue-500/20 cursor-pointer"
                  >
                    <Save size={15} className="!text-white" />
                    <span className="!text-white">Save & Apply Globally</span>
                  </button>
                </div>
              </div>

              {/* Grid Layout: Controls & Live Preview */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Left 2 Cols: Customization Controls */}
                <div className="lg:col-span-2 space-y-6">
                  
                  {/* 1. BRAND PALETTE */}
                  <div className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                    <div className="flex items-center gap-2 text-slate-900 dark:text-white font-extrabold text-sm">
                      <Palette size={18} className="text-blue-600 dark:text-cyan-400" />
                      <span>Brand Color Palette</span>
                    </div>

                    {/* Preset Swatches */}
                    <div className="space-y-2">
                      <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                        Quick Preset Palettes
                      </span>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {themePresets.map((p, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setThemeForm({ ...themeForm, primaryColor: p.primary, accentColor: p.accent, themePreset: p.preset as any })}
                            className={`p-2.5 rounded-xl border flex items-center gap-2 text-xs font-bold transition-all cursor-pointer ${
                              themeForm.primaryColor === p.primary
                                ? "border-blue-500 bg-blue-50/50 dark:bg-blue-500/10 text-blue-700 dark:text-cyan-300 shadow-xs"
                                : "border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                            }`}
                          >
                            <span className="w-4 h-4 rounded-full shrink-0 shadow-xs" style={{ backgroundColor: p.primary }} />
                            <span className="truncate">{p.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Color Pickers */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                      <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-2">
                        <label className="font-bold text-slate-700 dark:text-slate-300 text-xs block">
                          Primary Brand Color
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={themeForm.primaryColor}
                            onChange={(e) => setThemeForm({ ...themeForm, primaryColor: e.target.value })}
                            className="w-10 h-10 rounded-xl cursor-pointer border border-slate-300 dark:border-slate-600 bg-transparent p-0.5"
                          />
                          <input
                            type="text"
                            value={themeForm.primaryColor}
                            onChange={(e) => setThemeForm({ ...themeForm, primaryColor: e.target.value })}
                            className="flex-1 p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-xs font-mono font-bold"
                          />
                        </div>
                      </div>

                      <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-2">
                        <label className="font-bold text-slate-700 dark:text-slate-300 text-xs block">
                          Secondary Accent Color
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={themeForm.accentColor}
                            onChange={(e) => setThemeForm({ ...themeForm, accentColor: e.target.value })}
                            className="w-10 h-10 rounded-xl cursor-pointer border border-slate-300 dark:border-slate-600 bg-transparent p-0.5"
                          />
                          <input
                            type="text"
                            value={themeForm.accentColor}
                            onChange={(e) => setThemeForm({ ...themeForm, accentColor: e.target.value })}
                            className="flex-1 p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-xs font-mono font-bold"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 2. TYPOGRAPHY & CORNER RADIUS */}
                  <div className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                    <div className="flex items-center gap-2 text-slate-900 dark:text-white font-extrabold text-sm">
                      <Layers size={18} className="text-purple-600 dark:text-purple-400" />
                      <span>Typography & Corner Radius</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="font-bold text-xs text-slate-700 dark:text-slate-300 block">
                          Primary Heading Font
                        </label>
                        <select
                          value={themeForm.fontHeading}
                          onChange={(e) => setThemeForm({ ...themeForm, fontHeading: e.target.value })}
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs font-bold"
                        >
                          <option value="'Space Grotesk', sans-serif">Space Grotesk (Tech Modern)</option>
                          <option value="'Inter', sans-serif">Inter (Clean Enterprise)</option>
                          <option value="'Outfit', sans-serif">Outfit (Geometric Bold)</option>
                          <option value="'Plus Jakarta Sans', sans-serif">Plus Jakarta Sans (SaaS Standard)</option>
                          <option value="'Poppins', sans-serif">Poppins (Rounded Playful)</option>
                          <option value="'DM Sans', sans-serif">DM Sans (Minimalist)</option>
                        </select>
                      </div>

                      <div className="space-y-1.5">
                        <label className="font-bold text-xs text-slate-700 dark:text-slate-300 block">
                          Body Text Font
                        </label>
                        <select
                          value={themeForm.fontBody}
                          onChange={(e) => setThemeForm({ ...themeForm, fontBody: e.target.value })}
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs font-bold"
                        >
                          <option value="'DM Sans', sans-serif">DM Sans (High Readability)</option>
                          <option value="'Inter', sans-serif">Inter (UI Standard)</option>
                          <option value="'Roboto', sans-serif">Roboto (Clean Neutral)</option>
                          <option value="'Outfit', sans-serif">Outfit (Modern)</option>
                        </select>
                      </div>
                    </div>

                    {/* Corner Radius Selection */}
                    <div className="space-y-2 pt-2">
                      <label className="font-bold text-xs text-slate-700 dark:text-slate-300 block">
                        Card & Button Corner Radius
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                          { label: "Rounded Soft", value: "0.75rem", px: "12px" },
                          { label: "Rounded Modern", value: "1rem", px: "16px" },
                          { label: "Pill Heavy", value: "1.5rem", px: "24px" },
                          { label: "Sharp Minimal", value: "0.25rem", px: "4px" }
                        ].map((r, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setThemeForm({ ...themeForm, borderRadius: r.value })}
                            className={`p-2.5 rounded-xl border text-xs font-bold text-center transition-all cursor-pointer ${
                              themeForm.borderRadius === r.value
                                ? "border-blue-500 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-cyan-400 font-black shadow-xs"
                                : "border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                            }`}
                          >
                            <span>{r.label}</span>
                            <span className="text-[10px] text-slate-400 block font-normal">({r.px})</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* 3. SITE BRANDING & LOGO */}
                  <div className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                    <div className="flex items-center gap-2 text-slate-900 dark:text-white font-extrabold text-sm">
                      <ImageIcon size={18} className="text-emerald-600 dark:text-emerald-400" />
                      <span>Site Branding & Official Logo</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="font-bold text-xs text-slate-700 dark:text-slate-300 block">
                          Website Brand Name
                        </label>
                        <input
                          type="text"
                          value={themeForm.siteName}
                          onChange={(e) => setThemeForm({ ...themeForm, siteName: e.target.value })}
                          placeholder="Hickter | Technology"
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs font-bold"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="font-bold text-xs text-slate-700 dark:text-slate-300 block">
                          Site Logo Upload / URL
                        </label>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 p-1 flex items-center justify-center border border-slate-200 dark:border-slate-700 shrink-0">
                            <img src={themeForm.siteLogoUrl} alt="Logo" className="w-full h-full object-contain" />
                          </div>
                          <div className="flex-1 space-y-1">
                            <label className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-cyan-400 border border-blue-200 dark:border-blue-500/20 text-xs font-bold cursor-pointer hover:bg-blue-100 transition-all">
                              <UploadCloud size={13} />
                              <span>Upload from PC</span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e, (dataUrl) => setThemeForm({ ...themeForm, siteLogoUrl: dataUrl }))}
                              />
                            </label>
                            <input
                              type="text"
                              value={themeForm.siteLogoUrl}
                              onChange={(e) => setThemeForm({ ...themeForm, siteLogoUrl: e.target.value })}
                              placeholder="/assets/logo/dl.webp"
                              className="w-full p-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Right Col: Live Interactive Component Preview Box */}
                <div className="space-y-6">
                  <div className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-5 sticky top-20">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                      <span className="text-xs font-extrabold uppercase tracking-wider text-slate-400">
                        Live Preview Canvas
                      </span>
                      <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 rounded-full">
                        ● Real-Time
                      </span>
                    </div>

                    {/* Preview Card */}
                    <div
                      className="p-5 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 space-y-4 shadow-sm transition-all"
                      style={{ borderRadius: themeForm.borderRadius }}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className="px-2.5 py-1 text-[10px] font-bold rounded-full text-white"
                          style={{ backgroundColor: themeForm.accentColor }}
                        >
                          ● Accent Badge
                        </span>
                        <span className="text-[11px] text-slate-400 font-mono">
                          {themeForm.siteName.split("|")[0]}
                        </span>
                      </div>

                      <div className="space-y-1">
                        <h4
                          className="text-lg font-extrabold text-slate-900 dark:text-white leading-tight"
                          style={{ fontFamily: themeForm.fontHeading }}
                        >
                          Next-Gen Software & Web Apps
                        </h4>
                        <p
                          className="text-xs text-slate-600 dark:text-slate-400 font-medium leading-relaxed"
                          style={{ fontFamily: themeForm.fontBody }}
                        >
                          Empowering modern digital commerce with ultra-fast sync engines, native mobile apps, and enterprise clouds.
                        </p>
                      </div>

                      <div className="pt-2 flex items-center gap-2">
                        <button
                          type="button"
                          className="flex-1 py-2 px-3 text-xs font-bold text-white shadow-md flex items-center justify-center gap-1.5 transition-all"
                          style={{
                            backgroundColor: themeForm.primaryColor,
                            borderRadius: themeForm.borderRadius
                          }}
                        >
                          <Sparkles size={13} />
                          <span>Primary Action</span>
                        </button>
                        <button
                          type="button"
                          className="py-2 px-3 text-xs font-bold border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900"
                          style={{ borderRadius: themeForm.borderRadius }}
                        >
                          Outline
                        </button>
                      </div>
                    </div>

                    <div className="p-3.5 rounded-2xl bg-blue-50/60 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 text-[11px] text-blue-700 dark:text-cyan-300 space-y-1">
                      <p className="font-bold">⚡ How This Works:</p>
                      <p className="text-slate-600 dark:text-slate-400">
                        When you click <strong>Save & Apply Globally</strong>, the selected primary color, font family, and radius are written to CSS variables and saved in local storage. All visitors will see your custom look immediately!
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleSaveTheme}
                      className="w-full py-3 rounded-xl font-bold text-xs text-white shadow-lg flex items-center justify-center gap-2 cursor-pointer transition-all hover:opacity-95"
                      style={{
                        backgroundColor: themeForm.primaryColor,
                        borderRadius: themeForm.borderRadius
                      }}
                    >
                      <Save size={15} />
                      <span>Save & Apply Globally</span>
                    </button>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 6: PAGES MANAGEMENT */}
          {/* ======================================================== */}
          {activeTab === "pages" && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                    Site Pages & Meta Management
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Control indexation, meta titles, publishing state, and SEO keywords for every page.
                  </p>
                </div>
              </div>

              <div className="rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 text-[11px] font-bold text-slate-400 uppercase">
                      <tr>
                        <th className="p-3.5">Page Name & Path</th>
                        <th className="p-3.5">SEO Title Tag</th>
                        <th className="p-3.5">Indexing</th>
                        <th className="p-3.5">Publish Status</th>
                        <th className="p-3.5">Last Modified</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                      {pages.map((p) => (
                        <tr key={p.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                          <td className="p-3.5">
                            <span className="font-extrabold text-slate-900 dark:text-white block">
                              {p.name}
                            </span>
                            <span className="font-mono text-[11px] text-blue-600 dark:text-cyan-400 block">
                              {p.path}
                            </span>
                          </td>
                          <td className="p-3.5 max-w-xs text-slate-600 dark:text-slate-300 font-medium truncate">
                            {p.title}
                          </td>
                          <td className="p-3.5">
                            <button
                              onClick={() => {
                                updatePage(p.id, { isIndexed: !p.isIndexed });
                                showToast(`${p.name} indexing toggled!`);
                              }}
                              className={`px-2.5 py-1 rounded-full text-[10px] font-bold cursor-pointer ${
                                p.isIndexed
                                  ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20"
                                  : "bg-slate-100 dark:bg-slate-800 text-slate-500"
                              }`}
                            >
                              {p.isIndexed ? "Index / DoFollow" : "NoIndex"}
                            </button>
                          </td>
                          <td className="p-3.5">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-cyan-400">
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                              {p.isPublished ? "Live Published" : "Draft"}
                            </span>
                          </td>
                          <td className="p-3.5 text-slate-400 font-mono text-[11px]">
                            {p.lastModified}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 7: CUSTOM MENU SECTION */}
          {/* ======================================================== */}
          {activeTab === "menu" && (
            <div className="space-y-6 max-w-4xl">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                    Custom Menu & Navigation Bar Manager
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Add custom links, change order, and toggle visibility on the public navigation bar.
                  </p>
                </div>

                <button
                  onClick={() => setIsAddMenuOpen(true)}
                  className="btn-primary-blue py-2.5 px-4 text-xs font-bold rounded-xl flex items-center gap-2 !text-white cursor-pointer shadow-md"
                >
                  <Plus size={16} className="!text-white" />
                  <span className="!text-white">Add Custom Link</span>
                </button>
              </div>

              {/* Menu Items List */}
              <div className="space-y-2.5">
                {menuItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs flex items-center justify-between gap-4 text-xs"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 font-black flex items-center justify-center text-[10px]">
                        {index + 1}
                      </span>
                      <div>
                        <span className="font-extrabold text-slate-900 dark:text-white text-sm block">
                          {item.name}
                        </span>
                        <span className="font-mono text-blue-600 dark:text-cyan-400 text-[11px]">
                          {item.path}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          const updated = menuItems.map((m) =>
                            m.id === item.id ? { ...m, isVisible: !m.isVisible } : m
                          );
                          updateMenuItems(updated);
                          showToast(`Navigation visibility toggled for ${item.name}!`);
                        }}
                        className={`px-3 py-1 rounded-xl text-xs font-bold cursor-pointer transition-colors ${
                          item.isVisible
                            ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20"
                            : "bg-slate-100 dark:bg-slate-800 text-slate-400 border border-slate-200 dark:border-slate-700"
                        }`}
                      >
                        {item.isVisible ? "Visible in Nav" : "Hidden"}
                      </button>

                      {item.isCustom && (
                        <button
                          onClick={() => {
                            deleteMenuItem(item.id);
                            showToast("Custom menu item removed.");
                          }}
                          className="p-1.5 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 cursor-pointer"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* ADD MENU MODAL */}
              {isAddMenuOpen && (
                <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                  <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                      <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                        Add Custom Navigation Link
                      </h3>
                      <button onClick={() => setIsAddMenuOpen(false)} className="cursor-pointer">
                        <X size={18} />
                      </button>
                    </div>

                    <form onSubmit={handleAddMenuItem} className="space-y-3 text-xs">
                      <div className="space-y-1">
                        <label className="font-bold">Link Label / Display Name</label>
                        <input
                          type="text"
                          required
                          value={newMenuItem.name}
                          onChange={(e) => setNewMenuItem({ ...newMenuItem, name: e.target.value })}
                          placeholder="e.g. Careers, Blog, Client Portal"
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold">Target Route / External URL</label>
                        <input
                          type="text"
                          required
                          value={newMenuItem.path}
                          onChange={(e) => setNewMenuItem({ ...newMenuItem, path: e.target.value })}
                          placeholder="e.g. /careers or https://..."
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-mono"
                        />
                      </div>

                      <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setIsAddMenuOpen(false)}
                          className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 font-bold"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn-primary-blue px-5 py-2 rounded-xl font-bold !text-white"
                        >
                          Add Link
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 8: EMAIL SMTP SETTINGS */}
          {/* ======================================================== */}
          {activeTab === "smtp" && (
            <div className="space-y-6 max-w-4xl">
              <div>
                <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                  Email SMTP Server & Relay Configuration
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Configure outbound mail relay for enquiries, package bids, and client alerts.
                </p>
              </div>

              <form onSubmit={handleSaveSmtp} className="space-y-4 bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm text-xs">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5 sm:col-span-2">
                    <label className="font-bold">SMTP Host / Server Address</label>
                    <input
                      type="text"
                      required
                      value={smtpForm.host}
                      onChange={(e) => setSmtpForm({ ...smtpForm, host: e.target.value })}
                      placeholder="e.g. smtp.mailgun.org, smtp.sendgrid.net"
                      className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-mono"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-bold">Port</label>
                    <input
                      type="number"
                      required
                      value={smtpForm.port}
                      onChange={(e) => setSmtpForm({ ...smtpForm, port: parseInt(e.target.value) || 587 })}
                      className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <label className="font-bold">Encryption Security</label>
                    <select
                      value={smtpForm.encryption}
                      onChange={(e) => setSmtpForm({ ...smtpForm, encryption: e.target.value as any })}
                      className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-bold"
                    >
                      <option value="TLS">TLS (Port 587)</option>
                      <option value="SSL">SSL (Port 465)</option>
                      <option value="NONE">None (Port 25)</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-bold">SMTP Username / Auth ID</label>
                    <input
                      type="text"
                      required
                      value={smtpForm.user}
                      onChange={(e) => setSmtpForm({ ...smtpForm, user: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-mono"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-bold">SMTP Password / API Key</label>
                    <input
                      type="password"
                      required
                      value={smtpForm.pass}
                      onChange={(e) => setSmtpForm({ ...smtpForm, pass: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                  <div className="space-y-1.5">
                    <label className="font-bold">Sender Name</label>
                    <input
                      type="text"
                      value={smtpForm.fromName}
                      onChange={(e) => setSmtpForm({ ...smtpForm, fromName: e.target.value })}
                      placeholder="Hickter Team"
                      className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-bold">From Email Address</label>
                    <input
                      type="email"
                      value={smtpForm.fromEmail}
                      onChange={(e) => setSmtpForm({ ...smtpForm, fromEmail: e.target.value })}
                      placeholder="notifications@hickter.com"
                      className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-mono"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-bold">Admin Recipient Email</label>
                    <input
                      type="email"
                      value={smtpForm.adminEmail}
                      onChange={(e) => setSmtpForm({ ...smtpForm, adminEmail: e.target.value })}
                      placeholder="admin@hickter.com"
                      className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-mono"
                    />
                  </div>
                </div>

                {/* Test Result Message */}
                {testResult && (
                  <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-semibold flex items-center gap-2">
                    <CheckCircle2 size={16} className="shrink-0" />
                    <span>{testResult}</span>
                  </div>
                )}

                <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleTestSmtp}
                    disabled={testingSmtp}
                    className="px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 font-bold text-xs flex items-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    <Send size={14} className="text-blue-600 dark:text-cyan-400" />
                    <span>{testingSmtp ? "Dispatching Test Handshake..." : "Send Test Email"}</span>
                  </button>

                  <button
                    type="submit"
                    className="btn-primary-blue px-6 py-2.5 rounded-xl font-bold !text-white flex items-center gap-2 cursor-pointer shadow-md"
                  >
                    <Save size={14} className="!text-white" />
                    <span className="!text-white">Save SMTP Settings</span>
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 9: USER MANAGEMENT */}
          {/* ======================================================== */}
          {activeTab === "users" && (
            <div className="space-y-6 max-w-4xl">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                    User & Role Management
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Manage administrative accounts, access tokens, and developer credentials.
                  </p>
                </div>

                <button
                  onClick={() => setIsAddUserOpen(true)}
                  className="btn-primary-blue py-2.5 px-4 text-xs font-bold rounded-xl flex items-center gap-2 !text-white cursor-pointer shadow-md"
                >
                  <Plus size={16} className="!text-white" />
                  <span className="!text-white">Add Staff User</span>
                </button>
              </div>

              <div className="rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 text-[11px] font-bold text-slate-400 uppercase">
                      <tr>
                        <th className="p-3.5">User</th>
                        <th className="p-3.5">Username / ID</th>
                        <th className="p-3.5">Role</th>
                        <th className="p-3.5">Status</th>
                        <th className="p-3.5">Last Active</th>
                        <th className="p-3.5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {users.map((u) => (
                        <tr key={u.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                          <td className="p-3.5">
                            <span className="font-bold text-slate-900 dark:text-white block">
                              {u.name}
                            </span>
                            <span className="text-[11px] text-slate-400 font-mono">{u.email}</span>
                          </td>
                          <td className="p-3.5 font-mono font-bold text-blue-600 dark:text-cyan-400">
                            {u.username}
                          </td>
                          <td className="p-3.5">
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-cyan-300">
                              {u.role}
                            </span>
                          </td>
                          <td className="p-3.5">
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              {u.status}
                            </span>
                          </td>
                          <td className="p-3.5 text-slate-400 text-[11px] font-mono">
                            {u.lastLogin || "Never"}
                          </td>
                          <td className="p-3.5 text-right">
                            {u.username !== "superadmin" && (
                              <button
                                onClick={() => {
                                  if (window.confirm(`Remove user ${u.username}?`)) {
                                    deleteUser(u.id);
                                    showToast(`User ${u.username} deleted.`);
                                  }
                                }}
                                className="p-1.5 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 cursor-pointer"
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ADD USER MODAL */}
              {isAddUserOpen && (
                <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                  <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                      <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                        Add New Staff User
                      </h3>
                      <button onClick={() => setIsAddUserOpen(false)} className="cursor-pointer">
                        <X size={18} />
                      </button>
                    </div>

                    <form onSubmit={handleCreateUser} className="space-y-3 text-xs">
                      <div className="space-y-1">
                        <label className="font-bold">Full Name</label>
                        <input
                          type="text"
                          required
                          value={newUser.name}
                          onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                          placeholder="e.g. John Doe"
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold">Username</label>
                          <input
                            type="text"
                            required
                            value={newUser.username}
                            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                            placeholder="johndoe"
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-mono"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="font-bold">Role</label>
                          <select
                            value={newUser.role}
                            onChange={(e) => setNewUser({ ...newUser, role: e.target.value as any })}
                            className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-bold"
                          >
                            <option value="Admin">Admin</option>
                            <option value="Editor">Editor</option>
                            <option value="Developer">Developer</option>
                          </select>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold">Email Address</label>
                        <input
                          type="email"
                          required
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                          placeholder="john@hickter.com"
                          className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 font-mono"
                        />
                      </div>

                      <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setIsAddUserOpen(false)}
                          className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 font-bold"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn-primary-blue px-5 py-2 rounded-xl font-bold !text-white"
                        >
                          Add User
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 10: DEPLOY & BACKUP PROCESS */}
          {/* ======================================================== */}
          {activeTab === "deploy" && (
            <div className="space-y-6 max-w-4xl">
              <div>
                <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                  Deployment & Database Synchronization
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Export backups, restore site configurations, purge cache, and view live build instructions.
                </p>
              </div>

              {/* Cache & Sync Action Card */}
              <div className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">
                      Live Production Cache Re-sync
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Force DOM hydration and re-index all local catalog items.
                    </p>
                  </div>

                  <button
                    onClick={handlePurgeCache}
                    disabled={isPurgingCache}
                    className="btn-primary-blue px-4 py-2 text-xs font-bold rounded-xl flex items-center gap-2 !text-white cursor-pointer disabled:opacity-60 shadow-md"
                  >
                    <RotateCcw size={14} className={`!text-white ${isPurgingCache ? "animate-spin" : ""}`} />
                    <span className="!text-white">{isPurgingCache ? "Re-syncing..." : "Purge & Re-sync"}</span>
                  </button>
                </div>

                {cacheStatus && (
                  <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-semibold">
                    {cacheStatus}
                  </div>
                )}
              </div>

              {/* Database Import & Export */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Export */}
                <div className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-3 flex flex-col justify-between">
                  <div className="space-y-1.5">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-cyan-400 flex items-center justify-center">
                      <Download size={20} />
                    </div>
                    <h4 className="text-sm font-extrabold text-slate-900 dark:text-white">
                      Export Full Site Backup (JSON)
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Downloads products, pricing tiers, portfolio items, home configurations, and page metadata into a portable JSON snapshot.
                    </p>
                  </div>
                  <button
                    onClick={handleDownloadBackup}
                    className="w-full py-2.5 px-4 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-extrabold text-xs flex items-center justify-center gap-2 cursor-pointer shadow-md"
                  >
                    <Download size={15} />
                    <span>Download JSON Backup</span>
                  </button>
                </div>

                {/* Import */}
                <div className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-3">
                  <div className="space-y-1.5">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                      <Upload size={20} />
                    </div>
                    <h4 className="text-sm font-extrabold text-slate-900 dark:text-white">
                      Import / Restore Site Backup
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Paste a previously exported JSON string to immediately restore the entire site configuration.
                    </p>
                  </div>
                  <textarea
                    rows={3}
                    value={importJsonText}
                    onChange={(e) => setImportJsonText(e.target.value)}
                    placeholder="Paste JSON string here..."
                    className="w-full p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-[11px] font-mono"
                  />
                  <button
                    onClick={handleImportJson}
                    className="w-full py-2.5 px-4 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-extrabold text-xs flex items-center justify-center gap-2 cursor-pointer shadow-md"
                  >
                    <Upload size={15} />
                    <span>Restore From JSON</span>
                  </button>
                </div>
              </div>

              {/* Deployment Instructions Box */}
              <div className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4 text-xs">
                <h3 className="text-sm font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                  <Server size={16} className="text-blue-600 dark:text-cyan-400" />
                  <span>Deployment Pipeline & Build Instructions</span>
                </h3>

                <div className="space-y-2 text-slate-600 dark:text-slate-300 font-medium leading-relaxed">
                  <p>
                    <strong className="text-slate-900 dark:text-white">1. Local Build:</strong> Run{" "}
                    <code className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono text-blue-600 dark:text-cyan-400 font-bold">
                      npm run build
                    </code>{" "}
                    to compile static assets into the <code className="font-mono">build/</code> directory.
                  </p>
                  <p>
                    <strong className="text-slate-900 dark:text-white">2. Vercel / Netlify Deploy:</strong> Connect your Git repository. Set build command to <code className="font-mono">npm run build</code> and publish directory to <code className="font-mono">build</code>. Single Page Application routing redirects are handled automatically via <code className="font-mono">public/_redirects</code>.
                  </p>
                  <p>
                    <strong className="text-slate-900 dark:text-white">3. Apache / cPanel / Shared Hosting:</strong> Upload contents of the <code className="font-mono">build/</code> folder into <code className="font-mono">public_html/</code>. An <code className="font-mono">.htaccess</code> file with rewrite rules for React Router is pre-packaged.
                  </p>
                  <p>
                    <strong className="text-slate-900 dark:text-white">4. Node.js / VPS / Docker:</strong> Run with <code className="font-mono">serve -s build -l 3000</code> or deploy with PM2: <code className="font-mono">pm2 serve build 3000 --spa</code>.
                  </p>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}
